import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, IndianRupee, CalendarCheck,
  RefreshCw, ClipboardList, Loader2, ChevronDown, ChevronRight, Plus, Trash2,
  CheckCircle2, ShieldCheck, Pencil, Car, Package as PackageIcon,
  StickyNote, Tag, Send, MapPin, Calendar, Filter, UserPlus, X, Sparkles,
  Activity as ActivityIcon, UserCog, MessageSquarePlus, AlertCircle,
  ArrowRight, History as HistoryIcon, Mail, Phone, FileText, Layers,
  Users, PhoneOff, Bell, Ban, Clock, Link2, Copy, MessageCircle,
} from 'lucide-react';
import { serviceLeadsApi, servicePackagesApi, citiesApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatCurrencyAuto, activeCurrencySymbol } from '@/utils/currency';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';

const STATUS_FILTERS = [
  { key: '',          label: 'All' },
  { key: 'DRAFT_UNVERIFIED', label: 'Drafts' },
  { key: 'NEW',       label: 'New' },
  { key: 'CONTACTED', label: 'Contacted' },
  { key: 'QUOTED',    label: 'Quoted' },
  { key: 'PAYMENT_LINK_SENT', label: 'Link sent' },
  { key: 'BOOKED',    label: 'Booked' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

export const STATUS_BADGE = {
  DRAFT_UNVERIFIED: 'bg-slate-100 text-slate-600 border-slate-300 italic',
  OTP_PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  NEW:         'bg-blue-100 text-blue-700 border-blue-200',
  CONTACTED:   'bg-amber-100 text-amber-700 border-amber-200',
  QUOTED:      'bg-violet-100 text-violet-700 border-violet-200',
  PAYMENT_LINK_SENT: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  BOOKED:      'bg-emerald-100 text-emerald-700 border-emerald-200',
  ASSIGNED:    'bg-cyan-100 text-cyan-700 border-cyan-200',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  COMPLETED:   'bg-emerald-200 text-emerald-800 border-emerald-300',
  CANCELLED:   'bg-rose-100 text-rose-700 border-rose-200',
  CLOSED:      'bg-gray-200 text-gray-700 border-gray-300',
  // Conversation / follow-up sub-states (mirrors inspection-leads catalogue)
  RNR:                       'bg-orange-100 text-orange-700 border-orange-200',
  RNR1:                      'bg-orange-100 text-orange-700 border-orange-200',
  RNR2:                      'bg-orange-200 text-orange-800 border-orange-300',
  RNR3:                      'bg-red-100 text-red-700 border-red-200',
  FOLLOW_UP:                 'bg-yellow-100 text-yellow-700 border-yellow-200',
  WHATSAPP_FOLLOW_UP:        'bg-green-100 text-green-700 border-green-200',
  REPEAT_FOLLOW_UP:          'bg-yellow-200 text-yellow-800 border-yellow-300',
  HOT_LEAD:                  'bg-red-100 text-red-700 border-red-200',
  ESCALATION:                'bg-red-200 text-red-800 border-red-300',
  NOT_INTERESTED:            'bg-gray-100 text-gray-600 border-gray-200',
  DEAD_LEAD:                 'bg-gray-200 text-gray-700 border-gray-300',
  STOP:                      'bg-gray-200 text-gray-700 border-gray-300',
  OUT_OF_SERVICE_AREA:       'bg-gray-100 text-gray-600 border-gray-200',
  WRONG_NUMBER:              'bg-gray-100 text-gray-600 border-gray-200',
  PURCHASED_FROM_COMPETITOR: 'bg-gray-200 text-gray-700 border-gray-300',
};

const STATUS_LABEL = {
  DRAFT_UNVERIFIED: 'Draft',
  OTP_PENDING: 'OTP pending',
  PAYMENT_LINK_SENT: 'Link sent',
  IN_PROGRESS: 'In progress',
  RNR: 'RNR',
  RNR1: 'RNR1',
  RNR2: 'RNR2',
  RNR3: 'RNR3',
  FOLLOW_UP: 'Follow up',
  WHATSAPP_FOLLOW_UP: 'WhatsApp follow up',
  REPEAT_FOLLOW_UP: 'Repeat follow up',
  HOT_LEAD: 'Hot lead',
  NOT_INTERESTED: 'Not interested',
  DEAD_LEAD: 'Dead lead',
  OUT_OF_SERVICE_AREA: 'Out of service area',
  WRONG_NUMBER: 'Wrong number',
  PURCHASED_FROM_COMPETITOR: 'Purchased from competitor',
};
export const renderStatus = (s) => STATUS_LABEL[s] || s;

const fmtINR = (v) => v == null ? '—' : formatCurrencyAuto(Math.round(Number(v)));

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const fmtTimeOnly = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
};

// Placeholder slot strings the website used to send when the customer
// didn't pick a real time. Don't render them as if they were a customer
// preference — show "Not specified" instead. Backend now sanitizes these
// at write time (see public_carservice._sanitize_preferred_slot) but
// historical records may still have them.
const _GARBAGE_SLOTS = new Set([
  'tomorrow_morning', 'tomorrow_afternoon', 'tomorrow_evening',
  'today_morning', 'today_afternoon', 'today_evening',
  'morning', 'afternoon', 'evening', 'tbd', 'any', 'asap',
]);

const fmtSlot = (s) => {
  if (!s) return '—';
  const lower = String(s).trim().toLowerCase();
  if (_GARBAGE_SLOTS.has(lower)) return '—';
  // Pretty-print other underscore-style strings (rep-entered notes etc.)
  if (s.includes('_')) return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return fmtDateTime(s);
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};


const SERVICE_STAT_CARDS = [
  // Per CEO 2026-05-23: collapsed the 7-tile inbox grid down to a focused
  // 6-tile set that surfaces only the actionable buckets reps need to
  // triage every morning. Removed: Contacted, Quoted, Payment Link Sent,
  // Cancelled (still filterable from the Status dropdown if needed).
  { key: '',                  label: 'All Leads',      icon: ClipboardList,     bg: 'bg-violet-500' },
  // "New Leads" = unified intake bucket: DRAFT_UNVERIFIED (mobile typed)
  // + OTP_PENDING (awaiting OTP) + NEW (verified). Row OTP badges
  // differentiate. The list endpoint expands `status=NEW` to the $in set.
  { key: 'NEW',               label: 'New Leads',      icon: UserPlus,          bg: 'bg-blue-500',
    countKey: 'NEW_TOTAL' },
  // RNR tile = rolled-up RNR / RNR1 / RNR2 / RNR3 — the backend expands
  // `status=RNR` to a $in set so clicking the tile shows every variant.
  { key: 'RNR',               label: 'RNR',            icon: PhoneOff,          bg: 'bg-orange-500',
    countKey: 'RNR_TOTAL' },
  // Followup tile = FOLLOW_UP / WHATSAPP_FOLLOW_UP / REPEAT_FOLLOW_UP.
  { key: 'FOLLOW_UP',         label: 'Followup',       icon: Bell,              bg: 'bg-amber-500',
    countKey: 'FOLLOW_UP_TOTAL' },
  { key: 'NOT_INTERESTED',    label: 'Not interested', icon: Ban,               bg: 'bg-slate-500' },
  { key: 'BOOKED',            label: 'Booked',         icon: CheckCircle2,      bg: 'bg-teal-500' },
  // CEO 2026-05-30: surface unpaid payment links so reps see at-a-glance
  // how many customers received a Razorpay link but haven't paid yet.
  // `countKey: PAYMENT_LINKS_UNPAID` reads the rolled-up bucket from
  // /api/service-leads/counts. Clicking the tile filters the list to
  // status=PAYMENT_LINK_SENT (one-to-one match — the bucket IS that status).
  { key: 'PAYMENT_LINK_SENT', label: 'Payment links',  icon: Link2,             bg: 'bg-fuchsia-500',
    countKey: 'PAYMENT_LINKS_UNPAID' },
];

// Pre-OTP funnel tiles — surfaced separately so admins can see drop-off
// (e.g. "42 sessions, 0 mobiles entered" → website tracking broken).
// Clicking these tiles forces `include_drafts=1` on the list so the rows
// actually become visible.
const FUNNEL_CARDS = [
  { key: 'DRAFT_UNVERIFIED', label: 'Mobile entered',    icon: UserPlus,  bg: 'bg-slate-400',
    hint: 'Customer typed mobile but did not submit form' },
  { key: 'OTP_PENDING',      label: 'Awaiting OTP',      icon: Send,      bg: 'bg-slate-500',
    hint: 'Customer submitted form but did not enter OTP' },
];

const DATE_PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'last_7',     label: 'Last 7 Days' },
  { key: 'last_14',    label: 'Last 14 Days' },
  { key: 'this_week',  label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom' },
  { key: 'all',        label: 'All Time' },
];

const mapsLinkFor = (lead) => {
  const loc = lead.service_location || {};
  if (loc.latitude && loc.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`;
  }
  const q = [loc.address, lead.address, lead.city].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};


// Acquisition-channel badge styling. `source_channel` is derived server-side
// from source + source_meta (gclid / utm / fbclid). Used by the Source column.
const SOURCE_CHANNELS = [
  { key: 'google',   label: 'Google Ads' },
  { key: 'meta',     label: 'Meta' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'website',  label: 'Website' },
  { key: 'referral', label: 'Referral' },
  { key: 'others',   label: 'Direct/Others' },
];
const SOURCE_STYLE = {
  'Google Ads':    'bg-blue-100 text-blue-800 border-blue-200',
  'Meta':          'bg-indigo-100 text-indigo-800 border-indigo-200',
  'WhatsApp':      'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Website':       'bg-violet-100 text-violet-800 border-violet-200',
  'Referral':      'bg-amber-100 text-amber-800 border-amber-200',
  'Direct/Others': 'bg-gray-100 text-gray-600 border-gray-200',
};
function SourceBadge({ channel, testId }) {
  if (!channel) return <span className="text-gray-300">—</span>;
  const cls = SOURCE_STYLE[channel] || SOURCE_STYLE['Direct/Others'];
  return (
    <Badge className={`${cls} border text-[10px] px-1.5 py-0 whitespace-nowrap`} data-testid={testId}>
      {channel}
    </Badge>
  );
}

// Preset cancel reasons (mirror backend _CANCEL_REASON_LABELS).
const CANCEL_REASONS = [
  { code: 'customer_changed_plan', label: 'Customer changed plan' },
  { code: 'wrong_amount', label: 'Wrong amount' },
  { code: 'customer_declined', label: 'Customer declined' },
  { code: 'duplicate_link', label: 'Duplicate / re-send' },
  { code: 'other', label: 'Other' },
];
const cancelReasonLabel = (code) =>
  (CANCEL_REASONS.find((r) => r.code === code) || {}).label
  || (code === 'manual_cancel' ? 'Cancelled by rep' : code) || '';

const fmtDur = (secs) => {
  if (secs == null || secs <= 0) return 'expired';
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const waHref = (mobile, text) => {
  let d = String(mobile || '').replace(/\D/g, '');
  if (d.length === 10) d = '91' + d;
  return `https://wa.me/${d}?text=${encodeURIComponent(text)}`;
};

// ─────────────── Expiry-nudge banner ───────────────
// Surfaces active (unpaid) payment links about to lapse so a rep can follow up
// (WhatsApp nudge / copy link) before they expire. Scoped by role server-side.
function ExpiringLinksBanner() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await serviceLeadsApi.expiringLinks(6);
      setRows(data?.leads || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || dismissed || rows.length === 0) return null;

  const copyLink = (url) => {
    if (url) { navigator.clipboard?.writeText(url); toast.success('Payment link copied'); }
  };

  return (
    <Card className="border-amber-300 bg-amber-50 px-4 py-3" data-testid="expiring-links-banner">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
          <Clock className="h-4 w-4" />
          {rows.length} payment link{rows.length > 1 ? 's' : ''} expiring soon (within 6h)
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-amber-800 hover:bg-amber-100"
                  onClick={load} data-testid="expiring-links-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-amber-800 hover:bg-amber-100"
                  onClick={() => setDismissed(true)} data-testid="expiring-links-dismiss">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const amt = r.advance_amount || r.final_price || 0;
          const left = fmtDur(r.expires_in_seconds);
          const first = String(r.customer_name || '').split(' ')[0] || '';
          const msg = `Hi ${first}, a quick reminder — your Wisedrive payment link for ₹${Math.round(amt)} is expiring soon. You can pay here: ${r.razorpay_link_url}`;
          return (
            <div key={r.id}
                 className="flex items-center justify-between gap-2 rounded-md bg-white/70 border border-amber-200 px-2.5 py-1.5"
                 data-testid={`expiring-link-row-${r.id}`}>
              <button
                className="text-left min-w-0 flex-1 hover:underline"
                onClick={() => window.dispatchEvent(new CustomEvent('open-service-lead', { detail: r.id }))}>
                <div className="text-xs font-medium text-slate-800 truncate">{r.customer_name || r.mobile}</div>
                <div className="text-[11px] text-slate-500">
                  ₹{Math.round(amt)} · expires in <span className="font-semibold text-amber-700">{left}</span>
                  {r.assigned_to_name ? ` · ${r.assigned_to_name}` : ''}
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <a href={waHref(r.mobile, msg)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                   data-testid={`expiring-link-whatsapp-${r.id}`}>
                  <MessageCircle className="h-3 w-3" /> Nudge
                </a>
                <button
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                  onClick={() => copyLink(r.razorpay_link_url)}
                  data-testid={`expiring-link-copy-${r.id}`}>
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}


export default function ServiceLeadsPage() {
  const { user: currentUser } = useAuth();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  // Default the date filter to TODAY so reps see today's pipeline first,
  // not the entire historical inbox (was 'all' until 14 May 2026).
  // Users can still flip to 'week' / 'month' / 'all' from the chip row.
  const [datePreset, setDatePreset] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cities, setCities] = useState([]);
  const [list, setList] = useState({ rows: [], total: 0 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [statusCatalog, setStatusCatalog] = useState([]);
  const [transitions, setTransitions] = useState({});
  // Activity drawer + reassign popover state
  const [activityLead, setActivityLead] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);

  // 2026-06-03: Reminder modal state (parity with inspection LeadsPage).
  // Reps set a date/time/reason; the background scheduler fires a Twilio
  // WhatsApp ~10 min before reminder_date+time (IST) using the same
  // template the inspection vertical uses.
  const [reminderLead, setReminderLead] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    reminder_date: '', reminder_time: '', reminder_reason: '',
  });
  const [savingReminder, setSavingReminder] = useState(false);

  // ── Founder-only tools (Sonu, 2026-10-07) ───────────────────────────
  // `founderTools` is set from the BACKEND, not from a hardcoded email list:
  // /employee-counts 403s for everyone else, so the allowlist stays server-side
  // and adding a founder is an env change, not a deploy.
  const [founderTools, setFounderTools] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState('');   // user id | __unassigned__ | ''
  const [employeeCounts, setEmployeeCounts] = useState({ employees: [], unassigned: null });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);      // "select all N matching filters"
  const [reassignTargets, setReassignTargets] = useState([]);
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkTypedConfirm, setBulkTypedConfirm] = useState('');

  // Filters the server must re-apply for "select all matching". Mirrors
  // fetchList exactly so the count the founder sees is the set that moves.
  const activeFilters = useMemo(() => {
    const f = {};
    if (statusFilter) f.status = statusFilter;
    if (search.trim()) f.search = search.trim();
    if (onlyMine) f.only_mine = true;
    if (cityFilter) f.city = cityFilter;
    if (sourceFilter) f.channel = sourceFilter;
    if (employeeFilter) f.assigned_to = employeeFilter;
    const searchActive = !!search.trim();
    if (!searchActive && datePreset && datePreset !== 'all' && datePreset !== 'custom') {
      f.date_preset = datePreset;
    }
    if (!searchActive && datePreset === 'custom') {
      if (dateFrom) f.date_from = dateFrom;
      if (dateTo) f.date_to = dateTo;
    }
    return f;
  }, [statusFilter, search, onlyMine, cityFilter, sourceFilter, employeeFilter, datePreset, dateFrom, dateTo]);

  const selectionCount = allMatching ? (list.total || 0) : selectedIds.size;
  // A 4,600-lead move is irreversible in one click, so past this size the
  // founder has to type the number out.
  const BULK_TYPED_CONFIRM_OVER = 200;
  const needsTypedConfirm = selectionCount > BULK_TYPED_CONFIRM_OVER;

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAllMatching(false);
  }, []);

  const toggleOne = useCallback((id) => {
    setAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const runBulkReassign = async () => {
    setBulkBusy(true);
    try {
      const body = {
        assigned_to: bulkTarget,
        // Sent on every call. If the list shifted while the founder was
        // choosing, the server 409s instead of moving a different set.
        expected_count: selectionCount,
        note: `Bulk reassigned by ${currentUser?.name || currentUser?.email}`,
      };
      if (allMatching) {
        body.select_all_matching = true;
        body.filters = activeFilters;
      } else {
        body.lead_ids = Array.from(selectedIds);
      }
      const { data } = await serviceLeadsApi.bulkReassign(body);
      const bits = [`${data.moved.toLocaleString('en-IN')} moved to ${data.to?.name}`];
      if (data.skipped_already_assigned) {
        bits.push(`${data.skipped_already_assigned} already there`);
      }
      toast.success(bits.join(' · '));
      setBulkConfirmOpen(false);
      setBulkTarget('');
      clearSelection();
      fetchList(); fetchCounts(); fetchEmployeeCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reassign failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (onlyMine) params.only_mine = true;
      if (cityFilter) params.city = cityFilter;
      if (sourceFilter) params.channel = sourceFilter;
      // Founder-only employee filter. '' = everyone, '__unassigned__' = the
      // nobody-owns-it bucket (the backend resolves all three null shapes).
      if (employeeFilter) params.assigned_to = employeeFilter;
      // Search should always span all-time — a customer's record from
      // last month shouldn't be hidden because the date filter is "today".
      // Skip date params entirely when there's an active search.
      const searchActive = !!search.trim();
      if (!searchActive && datePreset && datePreset !== 'all' && datePreset !== 'custom') {
        params.date_preset = datePreset;
      }
      if (!searchActive && datePreset === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
      }
      // The backend now includes DRAFT_UNVERIFIED + OTP_PENDING in the
      // default list (and treats statusFilter='NEW' as the unified bucket
      // of those three statuses) — no need for include_drafts toggle.
      const r = await serviceLeadsApi.list(params);
      setList(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load service leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, onlyMine, cityFilter, sourceFilter, employeeFilter, datePreset, dateFrom, dateTo]);

  const fetchCounts = useCallback(async () => {
    try {
      // Mirror the list's date filter so the cards match the table
      const params = {};
      if (datePreset && datePreset !== 'all' && datePreset !== 'custom') {
        params.date_preset = datePreset;
      }
      if (datePreset === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
      }
      // Feature 3: with an employee picked, every status count describes THAT
      // employee — otherwise the tiles showed company-wide totals above a
      // single-rep table.
      if (employeeFilter) params.assigned_to = employeeFilter;
      const r = await serviceLeadsApi.counts(params);
      setCounts(r.data);
    } catch {/* silent */}
  }, [datePreset, dateFrom, dateTo, employeeFilter]);

  // Employee list + per-employee counts. The 403 for non-founders is the gate:
  // it flips `founderTools` off, which hides the dropdown names, the tick
  // boxes and the bulk bar in one go.
  const fetchEmployeeCounts = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (cityFilter) params.city = cityFilter;
      if (sourceFilter) params.channel = sourceFilter;
      if (search.trim()) params.search = search.trim();
      if (datePreset && datePreset !== 'all' && datePreset !== 'custom') {
        params.date_preset = datePreset;
      }
      if (datePreset === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const r = await serviceLeadsApi.employeeCounts(params);
      setEmployeeCounts(r.data || { employees: [], unassigned: null });
      setFounderTools(true);
    } catch (e) {
      if (e.response?.status === 403) setFounderTools(false);
    }
  }, [statusFilter, cityFilter, sourceFilter, search, datePreset, dateFrom, dateTo]);

  useEffect(() => { fetchEmployeeCounts(); }, [fetchEmployeeCounts]);

  useEffect(() => {
    if (!founderTools) return;
    serviceLeadsApi.reassignTargets()
      .then((r) => setReassignTargets(r.data?.targets || []))
      .catch(() => {});
  }, [founderTools]);

  // Any filter change invalidates a tick-box selection — the rows underneath
  // are different now, and "select all matching" would mean something else.
  useEffect(() => { clearSelection(); }, [activeFilters, clearSelection]);

  // Count shown beside each option in the "All Status" filter dropdown.
  // NEW / RNR / FOLLOW_UP are rolled-up families on the list endpoint, so we
  // mirror that with the *_TOTAL buckets; every other status maps 1:1 to its
  // raw aggregation count from /service-leads/counts.
  const statusOptionCount = (value) => {
    if (value === 'NEW') return counts.NEW_TOTAL ?? 0;
    if (value === 'RNR') return counts.RNR_TOTAL ?? 0;
    if (value === 'FOLLOW_UP') return counts.FOLLOW_UP_TOTAL ?? 0;
    return counts[value] ?? 0;
  };


  // Cities — fetched once for filter + new-lead dropdown
  useEffect(() => {
    citiesApi.getNames()
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.cities || []);
        const names = arr.map((x) => (typeof x === 'string' ? x : x.name)).filter(Boolean);
        setCities([...new Set(names)].sort());
      })
      .catch(() => {});
  }, []);

  // Status catalogue (pipeline + conversation states) — fetched once
  // so the row dropdown knows every option + which transitions are valid
  // for the lead's current state. Mirrors the inspection-leads UX.
  useEffect(() => {
    serviceLeadsApi.statuses()
      .then((r) => {
        setStatusCatalog(r.data?.statuses || []);
        setTransitions(r.data?.transitions || {});
      })
      .catch(() => {});
  }, []);

  // Status values that often masquerade as "duplicate cleanup" — if the rep
  // tries to flip a row to these AND there's another active row for the same
  // mobile, we surface a "Merge instead?" dialog so the dead-lead row doesn't
  // become a permanent ghost duplicate.
  const SOFT_KILL_STATUSES = ['DEAD_LEAD', 'NOT_INTERESTED'];

  // Update a lead's status from the row dropdown. Optimistic — patches
  // the row in local state first, then syncs from the server response.
  const changeStatus = useCallback(async (lead, newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    const prev = lead.status;

    // ── Soft-kill guard: if the rep is marking this lead as DEAD / NOT_INTERESTED
    // and there's another active row for the same mobile, prompt to merge.
    if (SOFT_KILL_STATUSES.includes(newStatus) && lead.mobile) {
      try {
        const r = await serviceLeadsApi.byCustomer(lead.mobile, { exclude: lead.id });
        const rows = r.data?.rows || [];
        const activeSiblings = rows.filter((rr) => rr.kind === 'active' || rr.kind === 'draft');
        if (activeSiblings.length > 0) {
          const sibling = activeSiblings[0];
          const siblingName = sibling.customer_name || 'unnamed lead';
          const action = window.confirm(
            `This mobile (${lead.mobile}) has ${activeSiblings.length} other active lead(s) — e.g. "${siblingName}" (${sibling.status}).\n\n` +
            `Marking this row as ${newStatus === 'DEAD_LEAD' ? 'Dead lead' : 'Not interested'} usually means you want to silence a duplicate.\n\n` +
            `OK = Merge all duplicates into one row (recommended)\n` +
            `Cancel = Just change status anyway (creates a ghost duplicate row)`,
          );
          if (action) {
            // User chose Merge
            try {
              const m = await serviceLeadsApi.dedupeCustomer(lead.mobile);
              const result = m.data || {};
              toast.success(
                `Merged ${result.merged ?? 0} duplicate(s). Kept lead ${(result.kept_id || '').slice(0, 8)} (${result.kept_status || '—'}).`,
              );
              fetchList();
              fetchCounts();
              return;
            } catch (me) {
              toast.error(`Merge failed: ${me?.response?.data?.detail || me?.message || 'unknown'}`);
              return;
            }
          }
          // User chose Cancel (= go ahead with status change anyway) — fall through
        }
      } catch (_) {
        // Sibling check failed → don't block the status change; just proceed
      }
    }

    // Optimistic update so the dropdown closes instantly
    setList((cur) => ({
      ...cur,
      rows: cur.rows.map((r) => r.id === lead.id ? { ...r, status: newStatus } : r),
    }));
    try {
      const r = await serviceLeadsApi.patch(lead.id, { status: newStatus });
      // Sync with server's authoritative copy
      setList((cur) => ({
        ...cur,
        rows: cur.rows.map((row) => row.id === lead.id ? { ...row, ...r.data } : row),
      }));
      toast.success(`Status → ${STATUS_LABEL[newStatus] || newStatus}`);
      fetchCounts();
    } catch (e) {
      // Roll back on failure
      setList((cur) => ({
        ...cur,
        rows: cur.rows.map((row) => row.id === lead.id ? { ...row, status: prev } : row),
      }));
      toast.error(e.response?.data?.detail || 'Failed to change status');
    }
  }, [fetchCounts, fetchList]);

  // Listen for "switch to another lead" events fired from CustomerHistory rows
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      if (id) setExpandedId(id);
    };
    window.addEventListener('open-service-lead', handler);
    return () => window.removeEventListener('open-service-lead', handler);
  }, []);

  // After a "Merge duplicates" run inside the lead detail, refresh the inbox
  // so the now-deleted rows disappear from the list.
  useEffect(() => {
    const refresh = () => { fetchList(); fetchCounts(); };
    window.addEventListener('refresh-service-leads', refresh);
    return () => window.removeEventListener('refresh-service-leads', refresh);
  }, [fetchList, fetchCounts]);

  useEffect(() => { fetchList(); fetchCounts(); }, [fetchList, fetchCounts]);

  // ── Reminder helpers (parity with inspection vertical) ─────────
  const openReminderModal = useCallback((lead) => {
    setReminderLead(lead);
    setReminderForm({
      reminder_date: lead.reminder_date || '',
      reminder_time: lead.reminder_time || '',
      reminder_reason: lead.reminder_reason || '',
    });
  }, []);

  const closeReminderModal = useCallback(() => {
    setReminderLead(null);
    setReminderForm({ reminder_date: '', reminder_time: '', reminder_reason: '' });
  }, []);

  const saveReminder = useCallback(async () => {
    if (!reminderLead) return;
    if (!reminderForm.reminder_date) {
      toast.error('Pick a date');
      return;
    }
    setSavingReminder(true);
    try {
      await serviceLeadsApi.setReminder(reminderLead.id, {
        reminder_date: reminderForm.reminder_date,
        reminder_time: reminderForm.reminder_time || '',
        reminder_reason: reminderForm.reminder_reason || '',
      });
      const when = reminderForm.reminder_time
        ? `${reminderForm.reminder_date} ${reminderForm.reminder_time}`
        : reminderForm.reminder_date;
      toast.success(`Reminder set for ${when} — rep will get a WhatsApp ~10 min before.`);
      closeReminderModal();
      fetchList();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to set reminder';
      toast.error(msg);
    } finally {
      setSavingReminder(false);
    }
  }, [reminderLead, reminderForm, closeReminderModal, fetchList]);

  const clearReminder = useCallback(async (lead) => {
    if (!window.confirm(`Cancel reminder for ${lead.name || 'this lead'}?`)) return;
    try {
      await serviceLeadsApi.clearReminder(lead.id);
      toast.success('Reminder cleared');
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear reminder');
    }
  }, [fetchList]);

  const toggleRow = async (lead) => {
    if (expandedId === lead.id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(lead.id);
    setExpandedDetail(null);
    setExpandedLoading(true);
    try {
      const r = await serviceLeadsApi.get(lead.id);
      setExpandedDetail(r.data);
    } catch (e) {
      toast.error('Failed to load lead details');
      setExpandedId(null);
    } finally {
      setExpandedLoading(false);
    }
  };

  const refreshExpanded = async () => {
    if (!expandedId) return;
    const r = await serviceLeadsApi.get(expandedId);
    setExpandedDetail(r.data);
    fetchList();
    fetchCounts();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="service-leads-page">
      <div className="max-w-[1400px] mx-auto px-6 pt-8 pb-6 space-y-6">

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Service Leads</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and track all your car-service bookings</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="lg"
              onClick={() => setNewLeadOpen(true)}
              className="gap-2 bg-blue-600 hover:bg-blue-700 h-11 px-5 rounded-lg shadow-md"
              data-testid="service-leads-new"
            >
              <Plus className="h-4 w-4" /> Add Lead
            </Button>
          </div>
        </div>

        {/* Inbox-level duplicate-customers banner — only visible to admins */}
        <DuplicateCustomersBanner currentUser={currentUser} />

        {/* Expiry nudge — active payment links about to lapse (follow up first) */}
        <ExpiringLinksBanner />

        {/* Date range card */}
        <Card className="px-5 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="h-4 w-4 text-gray-400" />
              Date Range:
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {DATE_PRESETS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => {
                    setDatePreset(d.key);
                    if (d.key !== 'custom') { setDateFrom(''); setDateTo(''); }
                  }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    datePreset === d.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid={`date-preset-${d.key}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                       className="h-9 w-40" data-testid="filter-date-from" />
                <span className="text-gray-400">→</span>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                       className="h-9 w-40" data-testid="filter-date-to" />
              </div>
            )}
          </div>
        </Card>

        {/* Status count cards — unified intake. "New Leads" tile counts
            DRAFT_UNVERIFIED + OTP_PENDING + NEW together (NEW_TOTAL from
            backend); row badges in the table differentiate the OTP state. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {SERVICE_STAT_CARDS.map((s) => {
            const isActive = (s.key === '' && !statusFilter) || statusFilter === s.key;
            const count = s.key === ''
              ? (counts.TOTAL ?? list.total ?? 0)
              : (counts[s.countKey || s.key] ?? 0);
            const Icon = s.icon;
            return (
              <button
                key={s.key || 'all'}
                onClick={() => setStatusFilter(s.key)}
                className={`bg-white rounded-xl border p-4 text-left transition-all hover:shadow-md ${
                  isActive ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
                data-testid={`stat-card-${s.key || 'all'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.bg}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-2xl font-bold text-gray-900 leading-none">{count}</div>
                    <div className="text-xs text-gray-600 mt-1.5 truncate">{s.label}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Diagnostic banner — only when "Today" preset is selected AND the
            entire All-Leads count is zero. Pre-2026-05-15 this gated only on
            DRAFT/OTP_PENDING/NEW being zero, but the banner kept firing on
            days when 9-15 leads arrived via the OTP-verified path and moved
            straight to downstream statuses (RNR / FOLLOW_UP / DEAD_LEAD /
            OUT_OF_SERVICE_AREA / PURCHASED_FROM_COMPETITOR). If ANY lead was
            captured today, the website tracker is working — don't nag. */}
        {datePreset === 'today'
          && (counts.TOTAL ?? list.total ?? 0) === 0
          && (counts.NEW_TOTAL ?? 0) === 0
          && (counts.DRAFT_UNVERIFIED ?? 0) === 0
          && (counts.OTP_PENDING ?? 0) === 0
          && (counts.NEW ?? 0) === 0 && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2"
            data-testid="funnel-zero-hint"
          >
            <span className="font-semibold">No website activity captured today.</span>
            <span>If Clarity / Google Ads show traffic, the landing page may not be firing <code className="bg-amber-100 px-1 rounded">POST /api/public/service-leads/draft</code>. Open <code>/tracker-health</code> to debug.</span>
          </div>
        )}

        {/* Filter bar — sticky just below the top nav (h-14) so status/city/
            source filters stay pinned while the long list scrolls. */}
        <Card className="px-4 py-3 sticky top-14 z-30 shadow-sm border-blue-100">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, mobile, RC number, or package…"
                className="pl-9 h-10 border-gray-200"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchList()}
                data-testid="service-leads-search"
              />
            </div>

            <Select
              value={onlyMine ? 'mine' : (employeeFilter || '__all__')}
              onValueChange={(v) => {
                setOnlyMine(v === 'mine');
                setEmployeeFilter(v === 'mine' || v === '__all__' ? '' : v);
              }}
            >
              <SelectTrigger className="h-10 w-56" data-testid="filter-employee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">
                  All Employees <span className="text-gray-400">({counts.TOTAL ?? 0})</span>
                </SelectItem>
                <SelectItem value="mine">My queue</SelectItem>
                {founderTools && employeeCounts.employees.length > 0 && (
                  <div className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                    Service sales executives
                  </div>
                )}
                {founderTools && employeeCounts.employees.map((e, i) => {
                  const prev = employeeCounts.employees[i - 1];
                  // Everyone past this line holds leads but isn't a current
                  // service rep — a leaver, or someone whose role changed.
                  // Their leads must stay findable, but they are not the
                  // people Sonu is looking for, so they sit below a divider.
                  const startsOthers = !e.is_service_rep && (!prev || prev.is_service_rep);
                  return (
                    <React.Fragment key={e.id}>
                      {startsOthers && (
                        <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold border-t mt-1">
                          Others holding leads
                        </div>
                      )}
                      <SelectItem value={e.id} data-testid={`filter-employee-opt-${e.id}`}>
                        {e.name}
                        {!e.is_active && <span className="text-red-500"> · ex</span>}
                        <span className="text-gray-400"> ({e.count})</span>
                      </SelectItem>
                    </React.Fragment>
                  );
                })}
                {founderTools && employeeCounts.unassigned && (
                  <SelectItem value={employeeCounts.unassigned.id} data-testid="filter-employee-opt-unassigned">
                    Unassigned <span className="text-gray-400">({employeeCounts.unassigned.count})</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            <Select value={statusFilter || '__all__'}
                    onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-44" data-testid="filter-status-dropdown">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">All Status <span className="text-gray-400">({counts.TOTAL ?? 0})</span></SelectItem>
                {statusCatalog.length > 0 ? (() => {
                  const groups = { pipeline: [], follow_up: [], lost: [] };
                  statusCatalog.forEach((s) => (groups[s.group] || groups.pipeline).push(s));
                  const labels = { pipeline: 'Pipeline', follow_up: 'Follow-up', lost: 'Lost / Closed' };
                  return ['pipeline', 'follow_up', 'lost'].flatMap((g) => {
                    if (!groups[g].length) return [];
                    return [
                      <div key={`fhdr-${g}`} className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{labels[g]}</div>,
                      ...groups[g].map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label} <span className="text-gray-400">({statusOptionCount(s.value)})</span></SelectItem>
                      )),
                    ];
                  });
                })() : (
                  STATUS_FILTERS.filter(s => s.key).map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label} <span className="text-gray-400">({statusOptionCount(s.key)})</span></SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            <Select value={cityFilter || '__all__'}
                    onValueChange={(v) => setCityFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-40" data-testid="filter-city">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">All Cities</SelectItem>
                {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={sourceFilter || '__all__'}
                    onValueChange={(v) => setSourceFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10 w-40" data-testid="filter-source">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Sources</SelectItem>
                {SOURCE_CHANNELS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="lg"
              className="h-10 gap-2"
              onClick={() => {
                setCityFilter(''); setStatusFilter(''); setDatePreset('all');
                setDateFrom(''); setDateTo(''); setSearch(''); setOnlyMine(false);
                setSourceFilter(''); setEmployeeFilter('');
              }}
              data-testid="filter-clear"
            >
              <X className="h-4 w-4" /> Reset
            </Button>
          </div>
        </Card>

        {/* Founder-only bulk reassignment bar. Appears only once rows are
            ticked, so it never competes with the filters for attention. */}
        {founderTools && selectionCount > 0 && (
          <Card
            className="px-4 py-3 border-indigo-200 bg-indigo-50/60"
            data-testid="bulk-reassign-bar"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <Users className="h-4 w-4 text-indigo-600 shrink-0" />
              <span className="text-sm font-semibold text-indigo-900" data-testid="bulk-selection-count">
                {selectionCount.toLocaleString('en-IN')} lead{selectionCount === 1 ? '' : 's'} selected
                {allMatching && ' (every lead matching these filters)'}
              </span>

              {!allMatching && (list.total || 0) > selectedIds.size && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-indigo-700 underline"
                  onClick={() => { setAllMatching(true); setSelectedIds(new Set()); }}
                  data-testid="bulk-select-all-matching"
                >
                  Select all {(list.total || 0).toLocaleString('en-IN')} matching these filters
                </Button>
              )}

              <div className="flex-1" />

              <Select value={bulkTarget} onValueChange={setBulkTarget}>
                <SelectTrigger className="h-9 w-60 bg-white" data-testid="bulk-reassign-target">
                  <SelectValue placeholder="Reassign to…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {reassignTargets.length === 0 && (
                    <div className="px-2 py-3 text-xs text-gray-500">
                      No eligible reps. Give the employee the
                      <strong> SERVICE_SALES_EXEC </strong> role first.
                    </div>
                  )}
                  {reassignTargets.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`bulk-target-opt-${t.id}`}>
                      {t.name}
                      {t.is_founder && <span className="text-gray-400"> · founder</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                size="sm"
                className="h-9 gap-2 bg-indigo-600 hover:bg-indigo-700"
                disabled={!bulkTarget || bulkBusy}
                onClick={() => { setBulkTypedConfirm(''); setBulkConfirmOpen(true); }}
                data-testid="bulk-reassign-submit"
              >
                <UserCog className="h-4 w-4" /> Reassign
              </Button>
              <Button
                variant="ghost" size="sm" className="h-9"
                onClick={clearSelection}
                data-testid="bulk-clear-selection"
              >
                Clear
              </Button>
            </div>
          </Card>
        )}

        {/* Data table */}
        <Card className="overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm table-fixed" data-testid="service-leads-table">
              <colgroup>
                {founderTools && <col style={{ width: '3%' }} />}
                <col style={{ width: '3%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '6%' }} />
              </colgroup>
              <thead className="bg-gray-50 border-b text-left [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-50">
                <tr>
                  {founderTools && (
                    <th className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-indigo-600"
                        aria-label="Select all rows on this page"
                        data-testid="bulk-select-page"
                        checked={list.rows.length > 0
                          && (allMatching || list.rows.every((r) => selectedIds.has(r.id)))}
                        onChange={(e) => {
                          setAllMatching(false);
                          setSelectedIds(e.target.checked
                            ? new Set(list.rows.map((r) => r.id))
                            : new Set());
                        }}
                      />
                    </th>
                  )}
                  <th className="px-3 py-3"></th>
                  <th className="px-3 py-3 font-medium text-gray-700">Lead date/time</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Customer / Source</th>
                  <th className="px-3 py-3 font-medium text-gray-700">City / Reminder</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Car details</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Package</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Requested slot</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Status</th>
                  <th className="px-3 py-3 font-medium text-gray-700">Assigned to</th>
                  <th className="px-3 py-3 font-medium text-gray-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && list.rows.length === 0 && (
                  <tr>
                    <td colSpan={founderTools ? 11 : 10} className="text-center text-gray-500 py-12">
                      <ClipboardList className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                      <div className="font-medium">No service leads yet</div>
                      <div className="text-xs mt-1">
                        Leads from the public landing page will appear here once verified.
                      </div>
                    </td>
                  </tr>
                )}
                {list.rows.map(lead => {
                  const isExpanded = expandedId === lead.id;
                  const otpVerified = !!lead.otp_verified_at;
                  // OTP state for the row badge:
                  //   DRAFT_UNVERIFIED → grey "Mobile only" (no OTP)
                  //   OTP_PENDING     → amber "OTP awaiting"
                  //   anything else with otp_verified_at set → green "OTP verified"
                  let otpBadge = null;
                  if (lead.status === 'DRAFT_UNVERIFIED') {
                    // Distinguish between bare mobile-only drafts and
                    // enriched drafts (where the customer kept filling the
                    // form — typed vehicle / RC / package — but never
                    // verified OTP). Pre-2026-05-15 both showed the same
                    // grey "Mobile only" badge which was misleading for
                    // rows that already had car details captured.
                    const isEnrichedDraft = Boolean(
                      lead.vehicle?.brand
                      || lead.vehicle?.model
                      || lead.vehicle?.rc_number
                      || lead.package_id
                    );
                    otpBadge = isEnrichedDraft ? (
                      <Badge
                        className="bg-blue-100 text-blue-800 border-blue-200 border text-[10px] gap-1 px-1.5 py-0"
                        title="Customer entered car details but did not verify OTP — call to convert"
                      >
                        Hot · No OTP
                      </Badge>
                    ) : (
                      <Badge
                        className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px] gap-1 px-1.5 py-0"
                        title="Customer typed mobile but did not submit form"
                      >
                        Mobile only
                      </Badge>
                    );
                  } else if (lead.status === 'OTP_PENDING') {
                    otpBadge = (
                      <Badge
                        className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px] gap-1 px-1.5 py-0"
                        title="Customer submitted form, awaiting OTP entry"
                      >
                        OTP awaiting
                      </Badge>
                    );
                  } else if (otpVerified) {
                    otpBadge = (
                      <Badge
                        className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px] gap-1 px-1.5 py-0"
                        title="OTP verified"
                      >
                        <ShieldCheck className="h-3 w-3" /> OTP
                      </Badge>
                    );
                  }
                  const v = lead.vehicle || {};
                  const carBits = [v.brand, v.model, v.body_class, v.fuel_type].filter(Boolean);
                  const lastActivity = (lead.activities || []).slice(-1)[0];
                  const statusHistoryLast = (lead.status_history || []).slice(-1)[0];

                  return (
                      <tr
                        key={lead.id}
                        onClick={() => toggleRow(lead)}
                        className={`border-b cursor-pointer hover:bg-blue-50/40 transition-colors [&>td]:align-top ${
                          isExpanded ? 'bg-blue-50/60' : ''
                        }`}
                        data-testid={`lead-row-${lead.id}`}
                      >
                        {founderTools && (
                          <td
                            className="px-3 py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer accent-indigo-600"
                              aria-label={`Select lead ${lead.customer_name || lead.mobile}`}
                              data-testid={`bulk-row-${lead.id}`}
                              checked={allMatching || selectedIds.has(lead.id)}
                              disabled={allMatching}
                              onChange={() => toggleOne(lead.id)}
                            />
                          </td>
                        )}
                        <td className="px-3 py-3 text-gray-400">
                          <ChevronRight className="h-4 w-4" />
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-gray-900 font-medium leading-tight">{fmtDateOnly(lead.created_at)}</div>
                          <div className="text-xs text-gray-600 leading-tight mt-0.5">{fmtTimeOnly(lead.created_at)}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{timeAgo(lead.created_at)}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-gray-900 break-words">{lead.customer_name || <span className="text-gray-400 italic">(no name yet)</span>}</span>
                            {otpBadge}
                          </div>
                          <div className="text-xs text-gray-500">{lead.mobile}</div>
                          <div className="mt-1.5">
                            <SourceBadge channel={lead.source_channel} testId={`lead-source-${lead.id}`} />
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {lead.city ? (
                            <div className="flex items-start gap-1 text-gray-700">
                              <MapPin className="h-3 w-3 text-rose-500 mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-900 truncate">{lead.city}</div>
                                {(lead.service_location?.address || lead.address) && (
                                  <a
                                    href={mapsLinkFor(lead)}
                                    target="_blank" rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] text-blue-600 hover:underline truncate block"
                                    title={lead.service_location?.address || lead.address}
                                  >
                                    {lead.service_location?.address || lead.address}
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                          <div className="mt-2 pt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                            <ReminderCell
                              lead={lead}
                              onSet={() => openReminderModal(lead)}
                              onClear={() => clearReminder(lead)}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {carBits.length > 0 ? (
                            <>
                              <div
                                className="text-gray-900 leading-snug line-clamp-2"
                                title={[v.brand, v.model, v.body_class, v.fuel_type].filter(Boolean).join(' ')}
                              >
                                {[v.brand, v.model].filter(Boolean).join(' ') || v.body_class}
                              </div>
                              <div className="text-[11px] text-gray-500 leading-snug truncate">
                                {[v.fuel_type, v.rc_number].filter(Boolean).join(' · ')}
                              </div>
                            </>
                          ) : (
                            <span
                              className="text-xs text-blue-600 underline-offset-2 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => { e.stopPropagation(); toggleRow(lead); }}
                            >
                              <Plus className="h-3 w-3" /> Add details
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {lead.package_name ? (
                            <>
                              <div className="text-gray-900">{lead.package_name}</div>
                              <div className="text-xs">
                                <span className="text-gray-500">Quoted </span>
                                <span className="font-medium text-gray-700">{fmtINR(lead.quoted_price)}</span>
                                {lead.final_price != null && (
                                  <>
                                    <span className="text-gray-300 mx-1">·</span>
                                    <span className="text-emerald-600 font-semibold">
                                      Final {fmtINR(lead.final_price)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            <span
                              className="text-xs text-blue-600 underline-offset-2 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => { e.stopPropagation(); toggleRow(lead); }}
                            >
                              <Plus className="h-3 w-3" /> Send quote
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {lead.preferred_slot ? (
                            <>
                              <div className="text-gray-700 font-medium">{fmtSlot(lead.preferred_slot)}</div>
                              <div className="text-[10px] text-gray-400">Preferred</div>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          {/* OTP_PENDING used to render as a read-only badge;
                              per CEO request (2026-05-18) reps need to be
                              able to call these customers and progress them
                              right away. Only DRAFT_UNVERIFIED rows stay
                              "tag-only" since they don't even have an OTP
                              attempt yet. */}
                          {statusCatalog.length > 0 ? (
                            <Select
                              value={lead.status}
                              onValueChange={(v) => changeStatus(lead, v)}
                            >
                              <SelectTrigger
                                className={`h-7 px-2 text-[11px] font-medium border ${STATUS_BADGE[lead.status] || 'bg-gray-100 text-gray-700 border-gray-200'} min-w-[120px]`}
                                data-testid={`status-select-${lead.id}`}
                              >
                                <SelectValue>{renderStatus(lead.status)}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const allowed = new Set(transitions[lead.status] || []);
                                  // Current value MUST be selectable so the trigger renders it
                                  allowed.add(lead.status);
                                  // Group by section for readability
                                  const groups = { pipeline: [], follow_up: [], lost: [] };
                                  statusCatalog.forEach((s) => {
                                    if (allowed.has(s.value)) {
                                      (groups[s.group] || groups.pipeline).push(s);
                                    }
                                  });
                                  const labels = { pipeline: 'Pipeline', follow_up: 'Follow-up', lost: 'Lost / Closed' };
                                  return ['pipeline', 'follow_up', 'lost'].flatMap((g) => {
                                    if (!groups[g].length) return [];
                                    return [
                                      <div key={`hdr-${g}`} className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{labels[g]}</div>,
                                      ...groups[g].map((s) => (
                                        <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>
                                          {s.label}
                                        </SelectItem>
                                      )),
                                    ];
                                  });
                                })()}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`${STATUS_BADGE[lead.status] || ''} border text-[10px]`}>
                              {renderStatus(lead.status)}
                            </Badge>
                          )}
                          {/* Payment indicator — surfaces ADVANCE_PAID / PAID
                              right next to the status so reps can see at a
                              glance which booked leads have actually paid. */}
                          {(lead.payment_status === 'PAID' || lead.payment_status === 'ADVANCE_PAID') && (
                            <Badge
                              className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] mt-1 ml-0 inline-flex items-center gap-1"
                              data-testid={`payment-paid-${lead.id}`}
                            >
                              <CheckCircle2 className="h-2.5 w-2.5" />
                              {lead.payment_status === 'PAID'
                                ? `Paid${lead.final_price ? ' ' + fmtINR(lead.final_price) : ''}`
                                : `Advance ${lead.advance_amount ? fmtINR(lead.advance_amount) : 'Paid'}`}
                            </Badge>
                          )}
                          {/* Last status-change timestamp (status_history wins, falls back to updated_at) */}
                          {(statusHistoryLast?.at || lead.updated_at) && (
                            <div
                              className="text-[10px] text-gray-400 mt-1 leading-tight"
                              title={fmtDateTime(statusHistoryLast?.at || lead.updated_at)}
                              data-testid={`status-updated-${lead.id}`}
                            >
                              Updated {timeAgo(statusHistoryLast?.at || lead.updated_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <AssignedToCell
                            lead={lead}
                            currentUser={currentUser}
                            onReassign={() => setReassignFor(lead)}
                          />
                        </td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => setActivityLead(lead)}
                              title="View notes & activity"
                              className="h-7 px-2 rounded-md hover:bg-indigo-50 text-gray-500 hover:text-indigo-600 flex items-center gap-1 transition-colors"
                              data-testid={`activity-btn-${lead.id}`}
                            >
                              <HistoryIcon className="h-3.5 w-3.5" />
                              <span className="text-[11px] font-medium">View</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Activity drawer / reassign popover / inline note modal */}
      <ActivityDrawer
        lead={activityLead}
        onClose={() => setActivityLead(null)}
        onChanged={() => { fetchList(); fetchCounts(); }}
      />
      <ReassignPopover
        lead={reassignFor}
        currentUser={currentUser}
        onClose={() => setReassignFor(null)}
        onDone={(updatedLead) => {
          setList((cur) => ({
            ...cur,
            rows: cur.rows.map((r) => r.id === updatedLead.id ? { ...r, ...updatedLead } : r),
          }));
          setReassignFor(null);
        }}
      />

      {/* Founder-only: confirm a bulk reassignment. Always shows the exact
          count and destination, and above 200 leads demands the number typed
          out — one mis-click here would rewrite thousands of owners. */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-reassign-confirm">
          <DialogHeader>
            <DialogTitle>Reassign {selectionCount.toLocaleString('en-IN')} lead{selectionCount === 1 ? '' : 's'}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-gray-50 border px-3 py-2">
              <div className="flex items-center gap-2 text-gray-800">
                <span className="font-semibold">
                  {selectionCount.toLocaleString('en-IN')} lead{selectionCount === 1 ? '' : 's'}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                <span className="font-semibold">
                  {reassignTargets.find((t) => t.id === bulkTarget)?.name || '—'}
                </span>
              </div>
              {allMatching && (
                <div className="text-xs text-gray-500 mt-1">
                  Everything matching the filters on screen, not just this page.
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600">
              The customer is not notified and the new owner is not messaged.
              Each lead keeps a record of who moved it.
            </p>
            {needsTypedConfirm && (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Type <strong>{selectionCount}</strong> to confirm
                </Label>
                <Input
                  value={bulkTypedConfirm}
                  onChange={(e) => setBulkTypedConfirm(e.target.value)}
                  placeholder={String(selectionCount)}
                  data-testid="bulk-reassign-typed-confirm"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} data-testid="bulk-reassign-cancel">
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              disabled={bulkBusy || (needsTypedConfirm && bulkTypedConfirm.trim() !== String(selectionCount))}
              onClick={runBulkReassign}
              data-testid="bulk-reassign-confirm-submit"
            >
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-06-03: Reminder modal — schedules a WhatsApp to the
          assigned rep ~10 min before the chosen date/time. Same UX
          contract as the inspection-vertical LeadsPage modal. */}
      <Dialog
        open={!!reminderLead}
        onOpenChange={(v) => { if (!v) closeReminderModal(); }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="service-lead-reminder-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-600" />
              Set follow-up reminder
            </DialogTitle>
          </DialogHeader>
          {reminderLead && (
            <div className="space-y-4 px-1 py-2">
              <div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
                Reminder will be sent via WhatsApp to{' '}
                <span className="font-medium text-gray-900">
                  {reminderLead.assigned_to_name || 'the assigned rep'}
                </span>{' '}
                approximately <span className="font-medium">10 minutes before</span>{' '}
                the time you set below.
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border rounded text-sm"
                  value={reminderForm.reminder_date}
                  onChange={(e) => setReminderForm((f) => ({ ...f, reminder_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="service-reminder-date-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Time <span className="text-gray-400">(optional — IST)</span>
                </label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border rounded text-sm"
                  value={reminderForm.reminder_time}
                  onChange={(e) => setReminderForm((f) => ({ ...f, reminder_time: e.target.value }))}
                  data-testid="service-reminder-time-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Reason / note
                </label>
                <textarea
                  rows={2}
                  className="w-full px-3 py-2 border rounded text-sm resize-none"
                  placeholder="e.g. Customer asked for callback after lunch — quote follow-up"
                  value={reminderForm.reminder_reason}
                  onChange={(e) => setReminderForm((f) => ({ ...f, reminder_reason: e.target.value }))}
                  maxLength={200}
                  data-testid="service-reminder-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeReminderModal}
                  data-testid="service-reminder-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={saveReminder}
                  disabled={savingReminder || !reminderForm.reminder_date}
                  data-testid="service-reminder-save-btn"
                >
                  {savingReminder ? (
                    <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</>
                  ) : (
                    <><Bell className="h-3 w-3 mr-1" /> Set Reminder</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lead detail modal — replaces inline accordion for clarity */}
      <Dialog
        open={!!expandedId}
        onOpenChange={(v) => { if (!v) { setExpandedId(null); setExpandedDetail(null); } }}
      >
        <DialogContent
          className="max-w-6xl max-h-[92vh] overflow-y-auto p-0"
          data-testid="lead-detail-modal"
        >
          <DialogHeader className="px-5 py-3 sticky top-0 bg-blue-600 z-10 flex flex-row items-center justify-between rounded-t-lg">
            <DialogTitle className="flex items-center gap-3 text-white">
              {expandedDetail ? (
                <>
                  <span>{expandedDetail.customer_name}</span>
                  <span className="text-xs text-blue-100 font-normal">{expandedDetail.mobile}</span>
                  <Badge className={`${STATUS_BADGE[expandedDetail.status] || ''} border text-[10px]`}>
                    {renderStatus(expandedDetail.status)}
                  </Badge>
                </>
              ) : 'Loading lead…'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {expandedDetail && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={async () => {
                    // Pre-fill a fresh lead with this customer's details
                    try {
                      const body = {
                        customer_name: expandedDetail.customer_name,
                        mobile: expandedDetail.mobile,
                        email: expandedDetail.email || undefined,
                        city: expandedDetail.city || undefined,
                        address: expandedDetail.address || undefined,
                        vehicle: expandedDetail.vehicle || undefined,
                        service_location: expandedDetail.service_location || undefined,
                        auto_assign: true,
                      };
                      const r = await serviceLeadsApi.create(body);
                      toast.success('New service order created');
                      setExpandedId(r.data.id);
                      setExpandedDetail(r.data);
                      fetchList();
                      fetchCounts();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to create new lead');
                    }
                  }}
                  className="gap-1.5 bg-white text-blue-700 hover:bg-blue-50 h-8"
                  data-testid="book-another-service"
                >
                  <Plus className="h-3.5 w-3.5" /> Book another service
                </Button>
              )}
              <button
                type="button"
                onClick={() => { setExpandedId(null); setExpandedDetail(null); }}
                className="rounded-full hover:bg-blue-500 p-1.5 text-white"
                aria-label="Close"
                data-testid="lead-detail-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>
          <div className="p-5">
            {expandedLoading || !expandedDetail ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <ExpandedDetail
                lead={expandedDetail}
                onChanged={refreshExpanded}
                onClose={() => { setExpandedId(null); setExpandedDetail(null); }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Lead dialog (manual rep-initiated lead, no OTP) */}
      <NewLeadDialog
        open={newLeadOpen}
        onOpenChange={setNewLeadOpen}
        cities={cities}
        onCreated={() => {
          // After creating/upserting a lead, ALWAYS reset the date
          // filter to "today" — otherwise reps stare at a stale filter
          // (e.g. "yesterday" or "last week") and conclude the lead
          // wasn't saved when it actually was. May 2026 — Farzana fix.
          setDatePreset('today');
          fetchList();
          fetchCounts();
        }}
      />
    </div>
  );
}


// ─────────────── Expanded detail (inline, not a drawer) ───────────────

export function ExpandedDetail({ lead, onChanged, onClose }) {

  return (
    <div className="space-y-4" data-testid="expanded-detail">
      {/* Top: 3-column grid — Customer/Vehicle/Location | Select package | Pricing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Col 1: customer + vehicle + location */}
        <div className="space-y-3">
          <CustomerBlock lead={lead} onChanged={onChanged} />

          <CustomerHistory mobile={lead.mobile} excludeId={lead.id}
                           onSwitchLead={onChanged ? (id) => {
                             // Trigger the page-level open handler via a custom event
                             window.dispatchEvent(new CustomEvent('open-service-lead', { detail: id }));
                           } : undefined}
                           onLeadDeleted={(keepId) => {
                             // Current lead got merged away — swap to the keeper if id present,
                             // else just close this modal.
                             if (keepId) {
                               window.dispatchEvent(new CustomEvent('open-service-lead', { detail: keepId }));
                             } else {
                               onClose?.();
                             }
                           }} />

          <VehicleBlock lead={lead} onChanged={onChanged} />

          <ServiceLocationBlock lead={lead} onChanged={onChanged} />

          {/* Preferred slot — moved here (was under Select package) so it
              sits directly below Service Location, matching the customer's
              real-world flow: "where" + "when" together. */}
          {!['CANCELLED', 'CLOSED'].includes(lead.status) && (
            <PreferredSlotBlock lead={lead} onChanged={onChanged} />
          )}
        </div>

        {/* Col 2: select package */}
        <div className="space-y-3">
          <Block label="Select package" icon={PackageIcon}>
            {lead.package_name ? (
              <>
                {/* Compute net total AFTER discount + balance. Per CEO request
                    (2026-05-18): show plain (base − discount) math here, no
                    GST inflation, so reps and customers see the actual amount
                    owed at a glance. Tax breakdown lives in the Send Quote
                    panel only.

                    netTotal  = quoted_price − discount     (always)
                    balance   = max(0, netTotal − advance)  (always) */}
                {(() => {
                  const base = Number(lead.quoted_price) || 0;
                  const disc = Number(lead.discount) || 0;
                  const advance = Number(lead.advance_amount) || 0;
                  // Actual amount captured by Razorpay (set by webhook /
                  // recheck-payment). DIFFERENT from `advance_amount` which
                  // is the REQUESTED advance the rep set on the payment
                  // link. Was previously conflated as "Advance paid",
                  // making rep see "Advance paid ₹500" even when the
                  // customer hadn't actually paid anything.
                  const paid = Number(lead.paid_amount) || 0;
                  const payStatus = (lead.payment_status || '').toUpperCase();
                  const hasCaptured =
                    paid > 0 || ['ADVANCE_PAID', 'PAID', 'PARTIAL_PAID'].includes(payStatus);
                  const netTotal = Math.max(0, base - disc);
                  const balance = Math.max(0, netTotal - paid);  // balance from CAPTURED
                  return (
                    <>
                      <KV k="Package" v={lead.package_name} />
                      <KV k="Base" v={fmtINR(base)} />
                      {(lead.extras || []).length > 0 && (
                        <div className="mt-1 pl-2 border-l-2 border-slate-200">
                          {lead.extras.map((e, i) => (
                            <div key={i} className="flex justify-between text-xs text-gray-600">
                              <span>+ {e.name}</span><span>{fmtINR(e.price)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {disc > 0 && (
                        <KV k="Discount" v={
                          <span className="text-blue-600 font-medium">− {fmtINR(disc)}</span>
                        } />
                      )}
                      {(base > 0 || disc > 0) && (
                        <div className="border-t mt-2 pt-2">
                          <KV k={<span className="font-semibold">Total</span>} v={
                            <span className="text-emerald-600 font-semibold">{fmtINR(netTotal)}</span>
                          } />
                          {/* Show CAPTURED payment first when present. */}
                          {hasCaptured && (
                            <>
                              <KV
                                k={payStatus === 'PAID' ? 'Paid' : 'Advance paid'}
                                v={
                                  <span className="text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid={`pkg-paid-${lead.id}`}>
                                    <CheckCircle2 className="h-3 w-3" />
                                    {fmtINR(paid || advance)}
                                  </span>
                                }
                              />
                              {balance > 0 && (
                                <KV k="Balance to be paid" v={
                                  <span className="text-rose-700 font-semibold">{fmtINR(balance)}</span>
                                } />
                              )}
                            </>
                          )}
                          {/* Customer NOT paid yet but rep has typed an
                              advance / sent a link — clearly distinguish
                              this from a real capture. */}
                          {!hasCaptured && advance > 0 && (
                            <>
                              <KV k="Advance requested" v={
                                <span className="text-amber-700 font-semibold inline-flex items-center gap-1" data-testid={`pkg-advance-req-${lead.id}`}>
                                  <Clock className="h-3 w-3" />
                                  {fmtINR(advance)}
                                </span>
                              } />
                              <div className="text-[10px] text-amber-600 italic pl-2 -mt-1">
                                Awaiting customer payment
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
                <PackageItems packageId={lead.package_id} />
              </>
            ) : (
              <EligiblePackageCards lead={lead} onChanged={onChanged} />
            )}
            {!['CANCELLED', 'CLOSED'].includes(lead.status) && (
              <SlotPackageEdit lead={lead} onChanged={onChanged} />
            )}
          </Block>
        </div>

        {/* Col 3: Pricing form + Payment status (sits to the right of Select package).
            Pricing actions are NEVER restricted by lead status. Per user
            directive (May 16, 2026): show the QuoteForm for ALL leads —
            including CANCELLED / CLOSED / already-PAID and leads that
            already have a live razorpay_link_url. Ops needs the freedom
            to send a fresh link, top-up an additional amount, or take
            payment after a status change without any UI blockers. */}
        <div className="space-y-3">
          <QuoteForm lead={lead} onDone={onChanged} />

          {/* Payment status — appears whenever there's ANY payment activity:
              a link was sent, an amount was paid (full / advance), an advance
              was recorded, or the lead is already in the BOOKED funnel. The
              earlier check only fired on `PAID` / `LINK_SENT`, which left
              ADVANCE_PAID + manually-recorded advances invisible in the CRM. */}
          {(
            lead.razorpay_link_url ||
            lead.payment_status === 'PAID' ||
            lead.payment_status === 'ADVANCE_PAID' ||
            lead.payment_status === 'LINK_SENT' ||
            lead.payment_status === 'LINK_CANCELLED' ||
            lead.status === 'PAYMENT_LINK_SENT' ||
            lead.status === 'BOOKED' ||
            (lead.advance_amount != null && Number(lead.advance_amount) > 0)
          ) && (
            <Block label="Payment status" icon={IndianRupee}>
              <div className={`text-xs px-2 py-1.5 rounded inline-flex items-center gap-1.5 mb-2 ${
                lead.payment_status === 'PAID'
                  ? 'bg-emerald-100 text-emerald-700'
                  : lead.payment_status === 'ADVANCE_PAID'
                    ? 'bg-emerald-100 text-emerald-700'
                    : lead.payment_status === 'LINK_SENT'
                      ? 'bg-fuchsia-100 text-fuchsia-700'
                      : lead.payment_status === 'LINK_CANCELLED'
                        ? 'bg-rose-100 text-rose-700'
                        : 'bg-amber-100 text-amber-700'
              }`} data-testid="payment-status-badge">
                {lead.payment_status === 'LINK_CANCELLED'
                  ? <Ban className="h-3 w-3" />
                  : <CheckCircle2 className="h-3 w-3" />}
                {lead.payment_status === 'PAID'
                  ? `Paid${lead.final_price ? ` · ${fmtINR(lead.final_price)}` : ''}`
                  : lead.payment_status === 'ADVANCE_PAID'
                    ? `Advance Paid${lead.paid_amount ? ` · ${fmtINR(lead.paid_amount)}` : (lead.advance_amount ? ` · ${fmtINR(lead.advance_amount)}` : '')}`
                    : lead.payment_status === 'LINK_SENT'
                      ? 'Payment link sent · awaiting customer'
                      : lead.payment_status === 'LINK_CANCELLED'
                        ? 'Payment link cancelled'
                        : 'Pending'}
              </div>
              {lead.payment_method && <KV k="Method" v={lead.payment_method} />}
              {/* Show CAPTURED paid amount distinctly from REQUESTED advance.
                  Bug fix (2026-05-23): the modal used to show "Advance ₹X"
                  even when X = the requested advance with no actual capture,
                  making reps think a customer had paid when they hadn't. */}
              {Number(lead.paid_amount) > 0 ? (
                <KV k="Captured" v={
                  <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {fmtINR(lead.paid_amount)}
                  </span>
                } />
              ) : (lead.advance_amount != null && Number(lead.advance_amount) > 0) && (
                <KV k="Advance requested" v={
                  <span className="text-amber-700 font-semibold">{fmtINR(lead.advance_amount)}</span>
                } />
              )}
              {lead.balance_amount != null && (
                <KV k="Balance" v={fmtINR(lead.balance_amount)} />
              )}
              {lead.razorpay_link_url && (
                <div className="mt-2 border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Razorpay link
                  </div>
                  {lead.razorpay_link_expire_by && (
                    <div className="text-[11px] text-slate-500 mb-1 inline-flex items-center gap-1"
                         data-testid="payment-link-expiry">
                      <Clock className="h-3 w-3" />
                      {(() => {
                        const s = Number(lead.razorpay_link_expire_by) - Math.floor(Date.now() / 1000);
                        if (s <= 0) return 'Link expired';
                        const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
                        return `Valid for ${h > 0 ? `${h}h ` : ''}${m}m more`;
                      })()}
                    </div>
                  )}
                  <a href={lead.razorpay_link_url} target="_blank" rel="noreferrer"
                     className="text-xs text-blue-600 underline break-all"
                     data-testid="payment-link">
                    {lead.razorpay_link_url}
                  </a>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="flex-1 text-xs"
                            onClick={() => {
                              navigator.clipboard.writeText(lead.razorpay_link_url);
                              toast.success('Link copied');
                            }}>
                      Copy link
                    </Button>
                    <a className="flex-1"
                       href={`https://wa.me/91${lead.mobile}?text=${encodeURIComponent('Your Wisedrive payment link: ' + lead.razorpay_link_url)}`}
                       target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="w-full text-xs gap-1">
                        <Send className="h-3 w-3" /> Re-send WhatsApp
                      </Button>
                    </a>
                  </div>
                  {/* Manual Razorpay reconcile — pulls authoritative status
                      from Razorpay API and reconciles paid_amount /
                      payment_status when our webhook missed a delivery. */}
                  <RecheckRazorpayButton leadId={lead.id} onChanged={onChanged} />
                  {!['PAID', 'ADVANCE_PAID', 'PARTIAL_PAID'].includes((lead.payment_status || '').toUpperCase()) && (
                    <CancelLinkButton leadId={lead.id} onChanged={onChanged} />
                  )}
                </div>
              )}

              {lead.payment_status === 'LINK_CANCELLED' && (
                <div className="mt-2 border-t pt-2" data-testid="cancelled-link-block">
                  <div className="text-[11px] text-rose-700 mb-1 inline-flex items-center gap-1">
                    <Ban className="h-3 w-3" />
                    Link cancelled
                    {lead.link_cancel_reason ? ` · ${cancelReasonLabel(lead.link_cancel_reason)}` : ''}
                    {lead.link_cancel_reason_note ? ` — ${lead.link_cancel_reason_note}` : ''}
                  </div>
                  <ReissueLinkButton leadId={lead.id} onChanged={onChanged} />
                </div>
              )}
            </Block>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────── Recheck Razorpay button ───────────────
// Calls /service-leads/{id}/recheck-payment which pulls authoritative
// payment_link status from Razorpay and reconciles paid_amount /
// payment_status when our webhook missed (or never fired).
// CEO-requested 2026-05-23 ("Razorpay status check thoroughly").
function RecheckRazorpayButton({ leadId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const { data } = await serviceLeadsApi.recheckPayment(leadId);
      if (data?.changed) {
        const after = data.crm_after || {};
        toast.success(
          `Razorpay says: ${after.payment_status} · ${fmtINR(after.paid_amount || 0)} captured`,
          { duration: 6000 },
        );
        onChanged && onChanged();
      } else {
        toast.info('CRM already in sync with Razorpay. No changes.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recheck failed');
    } finally { setBusy(false); }
  };
  return (
    <Button
      size="sm" variant="outline"
      className="w-full text-xs gap-1 mt-2 border-amber-300 text-amber-800 hover:bg-amber-50"
      disabled={busy}
      onClick={handle}
      data-testid="recheck-razorpay"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      Recheck Razorpay status
    </Button>
  );
}


// ─────────────── Cancel Razorpay link (with reason) ───────────────
// Cancels the lead's active link on Razorpay so it can NEVER be paid, capturing
// WHY (saved to lead history), then flips the lead to LINK_CANCELLED.
function CancelLinkButton({ leadId, onChanged, className }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('customer_changed_plan');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await serviceLeadsApi.cancelPaymentLink(leadId, { reason, reason_note: note });
      toast.success('Payment link cancelled — it can no longer be paid');
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel the link');
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button
        size="sm" variant="outline"
        className={className || 'w-full text-xs gap-1 mt-2 border-rose-300 text-rose-700 hover:bg-rose-50'}
        disabled={busy}
        onClick={() => setOpen(true)}
        data-testid="cancel-razorpay-link"
      >
        <Ban className="h-3 w-3" /> Cancel payment link
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="cancel-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Ban className="h-4 w-4" /> Cancel payment link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              The customer will no longer be able to pay this link. Tell us why (saved to the lead history):
            </p>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1" data-testid="cancel-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map((r) => (
                    <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                        className="mt-1" placeholder="Any detail…" data-testid="cancel-reason-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy}>Keep link</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 gap-1"
                    onClick={submit} disabled={busy} data-testid="cancel-reason-confirm">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
              Cancel link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─────────────── Reissue link (one-tap, same amount) ───────────────
// Sends a fresh payment link using the lead's stored quote so the rep needn't
// re-enter the amount after a link was cancelled or expired.
function ReissueLinkButton({ leadId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const { data } = await serviceLeadsApi.reissuePaymentLink(leadId);
      toast.success(data?.razorpay_link_url ? 'New payment link sent on WhatsApp' : 'Link reissued');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reissue the link');
    } finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      className="w-full text-xs gap-1 mt-2 bg-emerald-600 hover:bg-emerald-700"
      disabled={busy}
      onClick={handle}
      data-testid="reissue-razorpay-link"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      Reissue link (same amount)
    </Button>
  );
}


// ─────────────── Service items list (live fetch by package_id) ───────────────

function PackageItems({ packageId }) {
  const [pkg, setPkg] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!packageId) { setPkg(null); return; }
    let alive = true;
    servicePackagesApi.get(packageId)
      .then((r) => alive && setPkg(r.data))
      .catch(() => alive && setPkg(null));
    return () => { alive = false; };
  }, [packageId]);

  if (!pkg) return null;
  const items = pkg.items || [];
  if (items.length === 0) return null;

  return (
    <div className="mt-3 border-t pt-2" data-testid="package-items-list">
      <button type="button"
              onClick={() => setOpen(o => !o)}
              className="flex items-center justify-between w-full text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1.5">
        <span className="inline-flex items-center gap-1">
          <ClipboardList className="h-3 w-3" />
          What's covered ({items.length} {items.length === 1 ? 'item' : 'items'})
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li key={it.id || i} className="text-xs">
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">{it.name}</div>
                  {it.description && (
                    <div className="text-slate-500 text-[11px] mt-0.5">{it.description}</div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─────────────── small UI helpers ───────────────

function Block({ label, icon: Icon, children, actions }) {
  return (
    <div className="bg-white rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
          {Icon && <Icon className="h-3 w-3" />} {label}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between text-sm py-0.5 gap-3">
      <span className="text-gray-500 flex-shrink-0">{k}</span>
      <span className="text-gray-900 font-medium text-right break-words">{v}</span>
    </div>
  );
}


// ─────────────── Vehicle block (RC lookup + edit + persist) ───────────────

const FUEL_OPTS = ['Petrol', 'Diesel', 'CNG'];
const BODY_OPTS = ['Hatchback', 'Sedan', 'Compact SUV', 'Mid SUV', 'Premium'];

function CustomerBlock({ lead, onChanged }) {
  const hasName = !!(lead.customer_name || '').trim();
  // Auto-open in edit mode when the row is a fresh draft with no name yet —
  // saves the rep a click. (Same UX as VehicleBlock when no vehicle is set.)
  const [edit, setEdit] = useState(!hasName);
  const [draft, setDraft] = useState({
    customer_name: lead.customer_name || '',
    email: lead.email || '',
    city: lead.city || '',
    address: lead.address || '',
  });
  const [saving, setSaving] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);

  // Pull cities catalogue once when entering edit mode
  useEffect(() => {
    if (!edit) return;
    citiesApi.getNames()
      .then((r) => {
        const rows = r.data?.cities || r.data || [];
        setCityOptions(rows.filter(Boolean));
      })
      .catch(() => setCityOptions([]));
  }, [edit]);

  const save = async () => {
    const patch = {};
    const name = (draft.customer_name || '').trim();
    const email = (draft.email || '').trim();
    const city = (draft.city || '').trim();
    const address = (draft.address || '').trim();
    if (name && name !== (lead.customer_name || '')) patch.customer_name = name;
    if (email !== (lead.email || '')) patch.email = email;
    if (city !== (lead.city || '')) patch.city = city;
    if (address !== (lead.address || '')) patch.address = address;
    if (Object.keys(patch).length === 0) {
      setEdit(false);
      return;
    }
    setSaving(true);
    try {
      await serviceLeadsApi.patch(lead.id, patch);
      toast.success('Customer details saved');
      setEdit(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft({
      customer_name: lead.customer_name || '',
      email: lead.email || '',
      city: lead.city || '',
      address: lead.address || '',
    });
    setEdit(false);
  };

  if (!edit) {
    return (
      <Block label="Customer" icon={ShieldCheck}
             actions={(
               <button
                 type="button"
                 onClick={() => setEdit(true)}
                 className="text-[11px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                 data-testid="customer-edit-btn"
               >
                 <Pencil className="h-3 w-3" /> Edit
               </button>
             )}>
        <KV k="Name" v={lead.customer_name || <span className="italic text-gray-400">no name yet</span>} />
        <KV k="Mobile" v={lead.mobile} />
        <KV k="Email" v={lead.email || '—'} />
        <KV k="City" v={lead.city || '—'} />
        {lead.address && <KV k="Address" v={lead.address} />}
        {lead.assigned_to_name && <KV k="Assigned to" v={lead.assigned_to_name} />}
      </Block>
    );
  }

  return (
    <Block label="Customer" icon={ShieldCheck}>
      <div className="space-y-2" data-testid="customer-edit-form">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-gray-500">Name</label>
          <input
            type="text"
            value={draft.customer_name}
            onChange={(e) => setDraft((p) => ({ ...p, customer_name: e.target.value }))}
            placeholder="Customer name"
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
            data-testid="customer-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-gray-500">Mobile</label>
          <div className="text-xs px-2 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-md">
            {lead.mobile} <span className="text-gray-400">(read-only)</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-gray-500">Email</label>
          <input
            type="email"
            value={draft.email}
            onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))}
            placeholder="customer@example.com"
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
            data-testid="customer-email-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-gray-500">City</label>
          <input
            type="text"
            list="customer-city-options"
            value={draft.city}
            onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))}
            placeholder="Start typing… (e.g. Bengaluru)"
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
            data-testid="customer-city-input"
          />
          <datalist id="customer-city-options">
            {cityOptions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-gray-500">Address (optional)</label>
          <textarea
            rows={2}
            value={draft.address}
            onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))}
            placeholder="House / street / landmark"
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-none"
            data-testid="customer-address-input"
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5"
            data-testid="customer-save-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700"
            data-testid="customer-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </Block>
  );
}


function VehicleBlock({ lead, onChanged }) {
  const v = lead.vehicle || {};
  const hasAny = v.brand || v.model || v.fuel_type || v.body_class || v.rc_number;
  const [edit, setEdit] = useState(!hasAny);
  // Default mode: "rc" if there's already an RC number, else "manual"
  const [mode, setMode] = useState(v.rc_number ? 'rc' : 'manual');
  const [draft, setDraft] = useState({
    rc_number: v.rc_number || '',
    brand: v.brand || '',
    model: v.model || '',
    fuel_type: v.fuel_type || '',
    body_class: v.body_class || '',
    year: v.year || '',
    kms: v.kms || '',
  });
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [makesCatalogue, setMakesCatalogue] = useState([]);

  // Pull catalogue once when entering edit mode (cascading dropdowns)
  useEffect(() => {
    if (!edit) return;
    servicePackagesApi.models()
      .then((r) => setMakesCatalogue(r.data?.makes || []))
      .catch(() => setMakesCatalogue([]));
  }, [edit]);

  const modelsForBrand = (brand) => {
    if (!brand) return [];
    const m = makesCatalogue.find(x => x.name === brand);
    return m?.models || [];
  };
  const fuelsForBrandModel = (brand, modelName) => {
    if (!brand || !modelName) return FUEL_OPTS.map(f => f.toUpperCase());
    const m = modelsForBrand(brand).find(x => x.name === modelName);
    return (m?.fuels && m.fuels.length) ? m.fuels : FUEL_OPTS.map(f => f.toUpperCase());
  };
  // Catalogue uses uppercase fuels (PETROL/DIESEL/...); UI shows Title-case.
  const titleFuel = (f) => f ? f.charAt(0).toUpperCase() + f.slice(1).toLowerCase() : '';

  const lookupRC = async () => {
    const rc = (draft.rc_number || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (rc.length < 6) {
      toast.error('Enter a valid RC number to look up');
      return;
    }
    setLooking(true);
    try {
      const r = await serviceLeadsApi.lookupRC(rc);
      const d = r.data || {};
      setDraft((prev) => ({
        ...prev,
        rc_number: d.rc_number || prev.rc_number,
        brand: d.brand || prev.brand,
        model: d.model || prev.model,
        fuel_type: d.fuel_type || prev.fuel_type,
        body_class: d.body_class || prev.body_class,
        year: d.year || prev.year,
      }));
      toast.success(d.source === 'cache' ? 'Loaded from cache' : 'Vehicle details fetched');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vehicle lookup failed');
    } finally { setLooking(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(draft).filter(([, val]) => val !== '' && val != null),
      );
      // Coerce numbers
      if (cleaned.year) cleaned.year = Number(cleaned.year);
      if (cleaned.kms)  cleaned.kms  = Number(cleaned.kms);
      await serviceLeadsApi.patch(lead.id, { vehicle: cleaned });
      toast.success('Vehicle saved');
      setEdit(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (!edit) {
    return (
      <Block label="Vehicle" icon={Car}>
        <KV k="RC" v={v.rc_number || '—'} />
        <KV k="Brand" v={v.brand || '—'} />
        <KV k="Model" v={v.model || v.body_class || '—'} />
        <KV k="Fuel" v={v.fuel_type || '—'} />
        {v.year && <KV k="Year" v={v.year} />}
        <Button variant="ghost" size="sm" className="mt-1 w-full justify-start text-blue-600"
                onClick={() => setEdit(true)} data-testid="edit-vehicle">
          <Pencil className="h-3 w-3 mr-1" /> Edit / Add details
        </Button>
      </Block>
    );
  }

  const brandOptions = makesCatalogue.length
    ? makesCatalogue.map(m => m.name)
    : [];
  const modelOptions = modelsForBrand(draft.brand);
  const fuelOptionsRaw = fuelsForBrandModel(draft.brand, draft.model);

  return (
    <Block label="Vehicle" icon={Car}>
      {/* Tab toggle: By Vehicle Number  |  By Make / Model / Fuel */}
      <div
        className="grid grid-cols-2 rounded-lg bg-slate-100 p-0.5 mb-3"
        role="tablist"
        data-testid="vehicle-mode-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'rc'}
          onClick={() => setMode('rc')}
          className={`text-xs font-medium py-1.5 rounded-md transition ${
            mode === 'rc'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="vehicle-mode-rc"
        >
          By Vehicle Number
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'manual'}
          onClick={() => setMode('manual')}
          className={`text-xs font-medium py-1.5 rounded-md transition ${
            mode === 'manual'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="vehicle-mode-manual"
        >
          By Make / Model / Fuel
        </button>
      </div>

      {mode === 'rc' && (
        <>
          <Label className="text-xs">RC Number</Label>
          <div className="flex gap-2">
            <Input
              value={draft.rc_number}
              onChange={e => setDraft(d => ({ ...d, rc_number: e.target.value.toUpperCase() }))}
              placeholder="KA01AB1234"
              data-testid="vehicle-rc"
            />
            <Button
              size="sm" variant="outline"
              onClick={lookupRC} disabled={looking || !draft.rc_number}
              data-testid="vehicle-rc-lookup"
            >
              {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
              Lookup
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Auto-fills brand, model, fuel, body via Surepass</p>

          {/* Read-out of resolved details after lookup */}
          {(draft.brand || draft.model) && (
            <div className="mt-2 p-2 bg-slate-50 rounded text-xs space-y-0.5" data-testid="vehicle-rc-readout">
              <div><span className="text-slate-500">Brand:</span> <span className="font-medium">{draft.brand || '—'}</span></div>
              <div><span className="text-slate-500">Model:</span> <span className="font-medium">{draft.model || '—'}</span></div>
              <div><span className="text-slate-500">Fuel:</span> <span className="font-medium">{draft.fuel_type || '—'}</span></div>
              {draft.body_class && <div><span className="text-slate-500">Body:</span> <span className="font-medium">{draft.body_class}</span></div>}
              {draft.year && <div><span className="text-slate-500">Year:</span> <span className="font-medium">{draft.year}</span></div>}
            </div>
          )}
        </>
      )}

      {mode === 'manual' && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Make *</Label>
            <Select
              value={draft.brand || ''}
              onValueChange={v => setDraft(d => ({ ...d, brand: v, model: '', fuel_type: '' }))}
            >
              <SelectTrigger data-testid="vehicle-make-select">
                <SelectValue placeholder="Pick a Make" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {brandOptions.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Model *</Label>
            <Select
              value={draft.model || ''}
              onValueChange={v => setDraft(d => ({ ...d, model: v, fuel_type: '' }))}
              disabled={!draft.brand}
            >
              <SelectTrigger data-testid="vehicle-model-select">
                <SelectValue placeholder={draft.brand ? 'Pick a Model' : 'Pick Make first'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {modelOptions.map(m => (
                  <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Fuel *</Label>
            <Select
              value={draft.fuel_type ? draft.fuel_type.toUpperCase() : ''}
              onValueChange={v => setDraft(d => ({ ...d, fuel_type: titleFuel(v) }))}
              disabled={!draft.model}
            >
              <SelectTrigger data-testid="vehicle-fuel-select">
                <SelectValue placeholder={draft.model ? 'Pick Fuel' : 'Pick Model first'} />
              </SelectTrigger>
              <SelectContent>
                {fuelOptionsRaw.map(f => <SelectItem key={f} value={f}>{titleFuel(f)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Body class is captured via Surepass auto-fill in RC mode and is
          optional otherwise — kept on the document but not editable inline
          (was removed from the form per UX simplification on 2026-05-10).
          Year + KMs likewise dropped from the form: Year already arrives via
          Surepass and is shown in the read-out tile above. */}

      <div className="flex gap-2 mt-2">
        {hasAny && (
          <Button variant="outline" size="sm" className="flex-1" onClick={() => setEdit(false)}>
            Cancel
          </Button>
        )}
        <Button size="sm" className="flex-1" onClick={save} disabled={saving} data-testid="vehicle-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </Block>
  );
}


// ─────────────── Add note inline ───────────────

function AddNoteInline({ leadId, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <Button variant="ghost" size="sm" className="w-full mt-2 text-blue-600"
              onClick={() => setOpen(true)} data-testid="open-add-note">
        <Plus className="h-3 w-3 mr-1" /> Add note
      </Button>
    );
  }
  return (
    <div className="mt-2 border-t pt-2">
      <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                placeholder="Add a quick note…" data-testid="add-note-input" />
      <div className="flex gap-2 mt-1">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => { setOpen(false); setNote(''); }}>
          Cancel
        </Button>
        <Button size="sm" className="flex-1" disabled={busy || !note.trim()}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await serviceLeadsApi.addActivity(leadId, { type: 'note', note });
                    toast.success('Note added');
                    setNote(''); setOpen(false); onDone();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to save note');
                  } finally { setBusy(false); }
                }}
                data-testid="add-note-save"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </div>
  );
}


// ─────────────── Quote form (clean: only money + slot, vehicle/package live in their own blocks) ───────────────

const GST_RATE = 0.18;
const DEFAULT_ADVANCE = 500;

function QuoteForm({ lead, onDone }) {
  const leadId = lead.id;

  const [open, setOpen] = useState(true);   // open by default per user request

  // Money state — hydrated from lead so the rep's previous selection
  // (GST toggle, advance amount, note) round-trips on every reopen.
  // Bug fix (2026-05-23): all of these were hard-coded constants, so
  // GST kept reverting to ON and advance kept resetting to ₹500 even
  // after the rep saved the quote.
  const [base, setBase] = useState(lead.quoted_price || 0);
  const [extras, setExtras] = useState(lead.extras || []);
  const [discount, setDiscount] = useState(lead.discount || 0);
  const [gstOn, setGstOn] = useState(
    lead.gst_applicable !== undefined ? !!lead.gst_applicable : true
  );
  const [advance, setAdvance] = useState(
    Number(lead.advance_amount) > 0 ? Number(lead.advance_amount) : DEFAULT_ADVANCE
  );
  const [note, setNote] = useState(lead.quote_note || '');

  // Slot + payment
  const [slot, setSlot] = useState(lead.preferred_slot || '');
  const [paymentMethod, setPaymentMethod] = useState('UPI');

  const [submitting, setSubmitting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [, setTick] = useState(0);  // re-render for the expiry countdown

  // ── Active-link guardrail state ──────────────────────────────────────────
  const _ps = (lead.payment_status || '').toUpperCase();
  const isPaidState = ['PAID', 'ADVANCE_PAID', 'PARTIAL_PAID'].includes(_ps);
  const expireBy = lead.razorpay_link_expire_by ? Number(lead.razorpay_link_expire_by) : null;
  const secsLeft = expireBy != null ? (expireBy - Math.floor(Date.now() / 1000)) : null;
  const linkExpired = secsLeft != null && secsLeft <= 0;
  // An active (payable) link exists that would be superseded by a new send.
  const hasActiveLink = !!lead.razorpay_link_id && !!lead.razorpay_link_url && !isPaidState;
  const activeLinkLive = hasActiveLink && !linkExpired;

  const fmtTimeLeft = (secs) => {
    if (secs == null) return '';
    if (secs <= 0) return 'expired';
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Tick the countdown every 30s while an active link is showing.
  useEffect(() => {
    if (!activeLinkLive) return;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [activeLinkLive]);

  // Re-sync ALL editable fields when the lead reloads (e.g. after Save,
  // after package switched in SlotPackageEdit, after Razorpay webhook).
  // Bug fix (2026-05-23): the previous `if (!open)` guard ALWAYS skipped
  // because the form opens by default, so `base` stayed stale at the
  // lead's original quoted_price even after the rep typed a new value
  // and clicked Save — making the breakdown segment look "stuck on the
  // previous value".
  useEffect(() => {
    setBase(lead.quoted_price || 0);
    setSlot(lead.preferred_slot || '');
    setExtras(lead.extras || []);
    setDiscount(lead.discount || 0);
    if (lead.gst_applicable !== undefined) setGstOn(!!lead.gst_applicable);
    if (Number(lead.advance_amount) > 0) setAdvance(Number(lead.advance_amount));
    if (lead.quote_note) setNote(lead.quote_note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.quoted_price, lead.preferred_slot, lead.gst_applicable,
      lead.advance_amount, lead.discount]);

  // Money calcs
  const extrasTotal = useMemo(
    () => extras.reduce((s, x) => s + Number(x.price || 0), 0),
    [extras],
  );
  const subTotal = useMemo(
    () => Math.max(0, Number(base || 0) + extrasTotal - Number(discount || 0)),
    [base, extrasTotal, discount],
  );
  const gstAmount = useMemo(
    () => (gstOn ? Math.round(subTotal * GST_RATE * 100) / 100 : 0),
    [gstOn, subTotal],
  );
  const total = useMemo(() => subTotal + gstAmount, [subTotal, gstAmount]);
  const balance = Math.max(0, total - Number(advance || 0));

  if (!open) {
    return (
      <Block label="Pricing" icon={IndianRupee}>
        {!lead.package_id && (
          <p className="text-[11px] text-amber-700 mb-2">
            Set a package via "Edit slot / package" first.
          </p>
        )}
        <Button size="sm" onClick={() => setOpen(true)}
                disabled={!lead.package_id}
                className="w-full gap-2"
                data-testid="open-quote-form">
          <IndianRupee className="h-3.5 w-3.5" /> Send quote / payment link
        </Button>
      </Block>
    );
  }

  const buildPayload = () => ({
    // The Quote form's `total` is post-GST; for /quote we want the
    // sub-total (Base + Extras − Discount) so `final_price` on the lead is
    // tax-exclusive and matches the Total shown in the Select-package panel.
    final_price: Number(base),
    extras: extras
      .filter(e => e.name && Number(e.price) >= 0)
      .map(e => ({ name: e.name, price: Number(e.price) })),
    discount: Number(discount || 0),
    note,
    // Persist rep's GST + advance choice so QuoteForm rehydrates with
    // them on reopen (CEO 2026-05-23 bug).
    gst_applicable: !!gstOn,
    advance_amount: Number(advance || 0),
  });

  const submitQuote = async () => {
    setSubmitting(true);
    try {
      // Persist the breakdown (base override + GST + advance) on the lead
      await serviceLeadsApi.patch(leadId, {
        quoted_price: Number(base),
        preferred_slot: slot || undefined,
      });
      await serviceLeadsApi.quote(leadId, buildPayload());
      toast.success('Quote saved');
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save quote');
    } finally { setSubmitting(false); }
  };

  const submitPaymentLink = async () => {
    if (!slot) { toast.error('Pick a preferred slot first'); return; }
    if (!advance || Number(advance) <= 0) { toast.error('Advance amount must be > 0'); return; }
    setSendingLink(true);
    try {
      await serviceLeadsApi.patch(leadId, {
        quoted_price: Number(base),
        preferred_slot: slot,
      });
      await serviceLeadsApi.quote(leadId, buildPayload());
      const r = await serviceLeadsApi.sendPaymentLink(leadId, {
        confirmed_slot: slot,
        discount: Number(discount || 0),
        payment_method: paymentMethod,
        advance_amount: Number(advance),
        gst_applicable: gstOn,
        gst_amount: gstAmount,
        total_amount: total,
        note,
        cancel_existing: activeLinkLive,  // cancel the old live link first (hard-block)
      });
      toast.success(r.data?.razorpay_link_url
        ? (activeLinkLive ? 'Old link cancelled · new payment link sent on WhatsApp'
                          : 'Payment link sent on WhatsApp')
        : 'Booking shared on WhatsApp');
      setOpen(false);
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === 'object') {
        toast.error(detail.message || 'An active payment link already exists — cancel it first');
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Could not send payment link');
      }
    } finally { setSendingLink(false); }
  };

  return (
    <Block label="Send quote" icon={IndianRupee}>
      {/* Base price */}
      <Label className="text-xs">Base price ({activeCurrencySymbol()})</Label>
      <Input type="number" value={base}
             onChange={e => setBase(e.target.value)}
             data-testid="quote-base-price" />

      {/* Extras */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Extras</Label>
          <Button size="sm" variant="ghost"
                  onClick={() => setExtras(e => [...e, { name: '', price: 0 }])}
                  data-testid="quote-add-extra">
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
        {extras.map((x, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2 mb-1">
            <Input placeholder="Item name" value={x.name}
                   onChange={e => setExtras(arr => arr.map((y, j) => j === i ? { ...y, name: e.target.value } : y))} />
            <Input type="number" placeholder={`${activeCurrencySymbol()} Price`} value={x.price}
                   onChange={e => setExtras(arr => arr.map((y, j) => j === i ? { ...y, price: e.target.value } : y))} />
            <Button size="sm" variant="ghost"
                    onClick={() => setExtras(arr => arr.filter((_, j) => j !== i))}>
              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
            </Button>
          </div>
        ))}
      </div>

      {/* Discount */}
      <div className="mt-3">
        <Label className="text-xs flex items-center gap-1 text-blue-700 font-semibold">
          <Tag className="h-3 w-3" /> Apply discount on package ({activeCurrencySymbol()})
        </Label>
        <Input
          type="number" min={0}
          value={discount}
          onChange={e => setDiscount(e.target.value)}
          placeholder="0"
          className="border-blue-200 focus-visible:ring-blue-300"
          data-testid="quote-discount"
        />
      </div>

      {/* Money breakdown */}
      <div className="mt-3 rounded border bg-slate-50 p-2.5 text-sm space-y-1"
           data-testid="quote-breakdown">
        <Row k="Base" v={fmtINR(Number(base) || 0)} />
        {extrasTotal > 0 && <Row k={`Extras (${extras.length})`} v={fmtINR(extrasTotal)} />}
        {Number(discount) > 0 && (
          <Row k="Discount" v={<span className="text-blue-600">− {fmtINR(discount)}</span>} />
        )}
        <Row k="Sub-total" v={<span className="font-medium">{fmtINR(subTotal)}</span>} />

        {/* GST toggle */}
        <label className="flex items-center justify-between cursor-pointer pt-1 border-t">
          <span className="flex items-center gap-2">
            <input
              type="checkbox" checked={gstOn}
              onChange={e => setGstOn(e.target.checked)}
              data-testid="quote-gst-toggle"
            />
            <span className="text-xs">GST 18%</span>
          </span>
          <span className={gstOn ? 'text-gray-700' : 'text-gray-400 line-through'}>
            {fmtINR(gstAmount || subTotal * GST_RATE)}
          </span>
        </label>

        <Row k={<span className="font-semibold">Total</span>}
             v={<span className="font-bold text-emerald-700">{fmtINR(total)}</span>} />
      </div>

      {/* Minimum advance */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Minimum advance ({activeCurrencySymbol()})</Label>
          <Input type="number" min={1} value={advance}
                 onChange={e => setAdvance(e.target.value)}
                 className="font-semibold"
                 data-testid="quote-advance" />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Customer pays this now to lock the slot.
          </p>
        </div>
        <div>
          <Label className="text-xs">Balance (on service day)</Label>
          <div className="px-3 py-2 text-sm border rounded bg-slate-50">
            {fmtINR(balance)}
          </div>
        </div>
      </div>

      <Textarea placeholder="Optional note for mechanic" rows={2} className="mt-3"
                value={note} onChange={e => setNote(e.target.value)} />

      {/* Payment method (slot is set in the Select package column) */}
      <div className="mt-3 border-t pt-3">
        {!slot && (
          <div className="text-[11px] text-amber-700 mb-2">
            Set a preferred slot in <b>Select package</b> column to enable payment link.
          </div>
        )}
        <Label className="text-xs">Payment</Label>
        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
          <SelectTrigger data-testid="quote-payment-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="UPI">UPI / Razorpay link</SelectItem>
            <SelectItem value="CARD">Card / Razorpay link</SelectItem>
            <SelectItem value="NETBANKING">Netbanking / Razorpay link</SelectItem>
            <SelectItem value="COD">Cash on completion</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active-link guardrail: show the existing live/expired link + cancel */}
      {hasActiveLink && (
        <div
          className={`mt-3 rounded-lg border p-2.5 text-xs ${
            activeLinkLive ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-slate-50'
          }`}
          data-testid="active-link-warning"
        >
          <div className={`flex items-center gap-1.5 font-semibold ${
            activeLinkLive ? 'text-amber-800' : 'text-slate-600'
          }`}>
            <AlertCircle className="h-3.5 w-3.5" />
            {activeLinkLive ? 'An active payment link already exists' : 'Previous link expired'}
          </div>
          <a href={lead.razorpay_link_url} target="_blank" rel="noreferrer"
             className="mt-1 block text-blue-600 underline break-all"
             data-testid="active-link-url">
            {lead.razorpay_link_url}
          </a>
          {expireBy != null && (
            <div className="mt-1 inline-flex items-center gap-1 text-slate-600"
                 data-testid="active-link-expiry">
              <Clock className="h-3 w-3" />
              {activeLinkLive ? <>Expires in <b className="text-amber-800">{fmtTimeLeft(secsLeft)}</b></> : 'Expired'}
            </div>
          )}
          {activeLinkLive && (
            <div className="mt-2 text-[11px] text-amber-800">
              Sending a new link will <b>cancel this one first</b> so the customer can't pay a superseded link.
            </div>
          )}
          <CancelLinkButton leadId={leadId} onChanged={onDone}
            className="mt-2 w-full gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" />
        </div>
      )}

      <div className="flex flex-col gap-2 mt-3">
        <Button
          size="sm"
          disabled={sendingLink || submitting || !base || !slot || total <= 0 || Number(advance) <= 0}
          className={`w-full gap-2 ${activeLinkLive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          onClick={submitPaymentLink}
          data-testid="quote-send-link"
        >
          {sendingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {activeLinkLive
            ? `Cancel active link & send new for ${fmtINR(advance || 0)}`
            : `Send payment link for ${fmtINR(advance || 0)} on WhatsApp`}
        </Button>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" variant="secondary"
                  disabled={submitting || sendingLink || !base}
                  onClick={submitQuote}
                  data-testid="quote-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save quote only'}
          </Button>
        </div>
      </div>
    </Block>
  );
}


function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-600">{k}</span>
      <span>{v}</span>
    </div>
  );
}





// ─────────────── Book form ───────────────

function BookForm({ leadId, basePrice, address: addrProp, onDone }) {
  const [open, setOpen] = useState(false);
  const [slot, setSlot] = useState('');
  const [address, setAddress] = useState(addrProp || '');
  const [paymentMethod, setPaymentMethod] = useState('UPI');
  const [genLink, setGenLink] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  if (!open) {
    return (
      <Block label="Book service" icon={CalendarCheck}>
        <Button size="sm" onClick={() => setOpen(true)}
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="open-book-form">
          <CalendarCheck className="h-3.5 w-3.5" /> Confirm booking
        </Button>
      </Block>
    );
  }
  return (
    <Block label="Book service" icon={CalendarCheck}>
      <Label className="text-xs">Preferred slot</Label>
      <Input type="datetime-local" value={slot} onChange={e => setSlot(e.target.value)}
             data-testid="book-slot-input" />

      <Label className="text-xs mt-2">Service address</Label>
      <Textarea rows={2} value={address} onChange={e => setAddress(e.target.value)} />

      <Label className="text-xs mt-2">Payment</Label>
      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
        <SelectTrigger data-testid="book-payment-method"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="UPI">UPI / Razorpay link</SelectItem>
          <SelectItem value="CARD">Card / Razorpay link</SelectItem>
          <SelectItem value="NETBANKING">Netbanking / Razorpay link</SelectItem>
          <SelectItem value="COD">Cash on completion</SelectItem>
        </SelectContent>
      </Select>

      {paymentMethod !== 'COD' && (
        <label className="flex items-center gap-2 mt-2 text-xs">
          <input type="checkbox" checked={genLink} onChange={e => setGenLink(e.target.checked)} />
          Generate Razorpay payment link & WhatsApp it to customer
        </label>
      )}

      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
        <Button size="sm" disabled={submitting || !slot} className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    // Sync preferred_slot with the slot user just picked
                    await serviceLeadsApi.patch(leadId, { preferred_slot: slot }).catch(() => {});
                    const r = await serviceLeadsApi.book(leadId, {
                      confirmed_slot: slot,
                      address: address || undefined,
                      payment_method: paymentMethod,
                      generate_payment_link: paymentMethod !== 'COD' && genLink,
                    });
                    toast.success(r.data.razorpay_link_url
                      ? 'Booked! Payment link sent.'
                      : 'Booked!');
                    setOpen(false);
                    onDone();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to book');
                  } finally { setSubmitting(false); }
                }}
                data-testid="book-submit"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm booking'}
        </Button>
      </div>
    </Block>
  );
}


// ─────────────── New Lead dialog (manual create, no OTP) ───────────────

function NewLeadDialog({ open, onOpenChange, cities, onCreated }) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [packageId, setPackageId] = useState('');
  const [preferredSlot, setPreferredSlot] = useState('');
  // Lead source — rep-picked attribution. Sent as `source` in POST body.
  const [source, setSource] = useState('');
  const [rcNumber, setRcNumber] = useState('');
  const [vBrand, setVBrand] = useState('');
  const [vModel, setVModel] = useState('');
  const [vFuel, setVFuel] = useState('');
  const [vBody, setVBody] = useState('');
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lookingRC, setLookingRC] = useState(false);

  useEffect(() => {
    if (!open) return;
    servicePackagesApi.list({ channel: 'crm', is_active: true })
      .then((r) => setPackages(r.data || []))
      .catch(() => {});
  }, [open]);

  const reset = () => {
    setName(''); setMobile(''); setEmail(''); setCity(''); setAddress('');
    setCoords({ lat: null, lng: null }); setPackageId(''); setPreferredSlot('');
    setRcNumber(''); setVBrand(''); setVModel(''); setVFuel(''); setVBody('');
    setSource('');
  };

  const lookupRC = async () => {
    const rc = (rcNumber || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (rc.length < 6) return toast.error('Enter a valid RC number');
    setLookingRC(true);
    try {
      const r = await serviceLeadsApi.lookupRC(rc);
      const d = r.data || {};
      setRcNumber(d.rc_number || rc);
      if (d.brand) setVBrand(d.brand);
      if (d.model) setVModel(d.model);
      if (d.fuel_type) setVFuel(d.fuel_type);
      if (d.body_class) setVBody(d.body_class);
      toast.success('Vehicle details loaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RC lookup failed');
    } finally { setLookingRC(false); }
  };

  const submit = async () => {
    if (!name.trim() || !mobile.trim()) {
      return toast.error('Name and mobile are required');
    }
    setSubmitting(true);
    try {
      const body = {
        customer_name: name.trim(),
        mobile: mobile.trim(),
        email: email.trim() || undefined,
        city: city || undefined,
        address: address || undefined,
        package_id: packageId || undefined,
        preferred_slot: preferredSlot || undefined,
        source: source || undefined,
        vehicle: (rcNumber || vBrand || vModel || vFuel || vBody) ? {
          rc_number: rcNumber || undefined,
          brand: vBrand || undefined,
          model: vModel || undefined,
          fuel_type: vFuel || undefined,
          body_class: vBody || undefined,
        } : undefined,
        service_location: (coords.lat && coords.lng) ? {
          address: address || undefined,
          latitude: coords.lat,
          longitude: coords.lng,
        } : undefined,
        auto_assign: true,
      };
      const r = await serviceLeadsApi.create(body);
      const data = r.data || {};
      const wasMerged = data._merge_action === "merged_into_existing";
      const assignee = data.assigned_to_name ? ` — assigned to ${data.assigned_to_name}` : "";
      if (wasMerged) {
        // Open lead already existed for this mobile — the backend
        // duplicate-guard upgraded it instead of inserting a sibling
        // row. Tell the rep so they don't think their click failed.
        toast.success(`Existing open lead updated for ${data.mobile || "this mobile"}${assignee}`);
      } else {
        toast.success(`Lead created${assignee}`);
      }
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create lead');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-blue-600" /> New service lead
          </DialogTitle>
          <p className="text-xs text-gray-500">
            Bypass OTP — sales rep creates the lead directly. Lead lands in NEW state.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Customer name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="new-lead-name" />
          </div>
          <div>
            <Label className="text-xs">Mobile *</Label>
            <Input value={mobile} onChange={e => setMobile(e.target.value)}
                   placeholder="10-digit number" data-testid="new-lead-mobile" />
          </div>
          <div>
            <Label className="text-xs">Email (optional)</Label>
            <Input value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">City</Label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="new-lead-city"><SelectValue placeholder="Pick a city" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Service location (Google Places)</Label>
            <PlacesAutocomplete
              value={address}
              onChange={setAddress}
              onSelect={(p) => {
                setAddress(p.address || '');
                if (p.latitude && p.longitude) setCoords({ lat: p.latitude, lng: p.longitude });
                if (p.city && !city) setCity(p.city);
              }}
              placeholder="Pick the customer's address from Google Maps"
            />
          </div>
        </div>

        {/* Vehicle */}
        <div className="border rounded p-3 bg-slate-50 mt-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Vehicle (optional)
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
            <div>
              <Label className="text-xs">RC Number</Label>
              <Input value={rcNumber} onChange={e => setRcNumber(e.target.value.toUpperCase())}
                     placeholder="KA01AB1234" data-testid="new-lead-rc" />
            </div>
            <Button size="sm" variant="outline" onClick={lookupRC} disabled={lookingRC || !rcNumber}
                    data-testid="new-lead-rc-lookup">
              {lookingRC ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Lookup'}
            </Button>
            <div>
              <Label className="text-xs">Brand</Label>
              <Input value={vBrand} onChange={e => setVBrand(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div>
              <Label className="text-xs">Model</Label>
              <Input value={vModel} onChange={e => setVModel(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Fuel</Label>
              <Select value={vFuel} onValueChange={setVFuel}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {['Petrol','Diesel','CNG','Electric','Hybrid'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <Select value={vBody} onValueChange={setVBody}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {['Hatchback','Sedan','Compact SUV','Mid SUV','Premium'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Package + slot + source */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs">Service package (optional)</Label>
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger data-testid="new-lead-package">
                <SelectValue placeholder="None — rep will quote later" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {packages.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · {p.category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Preferred slot</Label>
            <Input type="datetime-local" value={preferredSlot}
                   onChange={e => setPreferredSlot(e.target.value)}
                   data-testid="new-lead-slot" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">
              Source <span className="text-gray-400">(how did this customer reach us?)</span>
            </Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="new-lead-source">
                <SelectValue placeholder="Pick a source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GOOGLE">Google</SelectItem>
                <SelectItem value="META">Meta (Facebook / Instagram)</SelectItem>
                <SelectItem value="DIRECT_WHATSAPP">Direct WhatsApp</SelectItem>
                <SelectItem value="REFERRAL">Referral</SelectItem>
                <SelectItem value="OTHERS">Others</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !name || !mobile}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="new-lead-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Service location block (display + edit + map) ───────────────

function ServiceLocationBlock({ lead, onChanged }) {
  const loc = lead.service_location || {};
  const [edit, setEdit] = useState(false);
  const [address, setAddress] = useState(loc.address || lead.address || '');
  const [coords, setCoords] = useState({
    lat: loc.latitude || null, lng: loc.longitude || null,
  });
  const [landmark, setLandmark] = useState(loc.landmark || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await serviceLeadsApi.patch(lead.id, {
        service_location: {
          address,
          latitude: coords.lat || null,
          longitude: coords.lng || null,
          landmark: landmark || null,
        },
      });
      toast.success('Service location updated');
      setEdit(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save location');
    } finally { setSaving(false); }
  };

  if (!edit) {
    return (
      <Block label="Service location" icon={MapPin}>
        {address ? (
          <>
            <div className="text-sm text-gray-800 break-words">{address}</div>
            {landmark && <div className="text-xs text-gray-500 mt-0.5">Landmark: {landmark}</div>}
            <a href={mapsLinkFor(lead)} target="_blank" rel="noreferrer"
               className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1">
              Open in Google Maps →
            </a>
          </>
        ) : (
          <div className="text-xs text-gray-500">No service location set.</div>
        )}
        <Button variant="ghost" size="sm" className="mt-2 w-full justify-start text-blue-600"
                onClick={() => setEdit(true)} data-testid="edit-location">
          <Pencil className="h-3 w-3 mr-1" /> {address ? 'Edit' : 'Add'} location
        </Button>
      </Block>
    );
  }

  return (
    <Block label="Service location" icon={MapPin}>
      <Label className="text-xs">Address (Google Places)</Label>
      <PlacesAutocomplete
        value={address}
        onChange={setAddress}
        onSelect={(p) => {
          setAddress(p.address || '');
          if (p.latitude && p.longitude) setCoords({ lat: p.latitude, lng: p.longitude });
        }}
        placeholder="Search for the customer's address…"
      />
      <Label className="text-xs mt-2">Landmark (optional)</Label>
      <Input value={landmark} onChange={e => setLandmark(e.target.value)}
             placeholder="e.g. Near KFC" />
      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" className="flex-1"
                onClick={() => { setEdit(false); }}>Cancel</Button>
        <Button size="sm" className="flex-1" onClick={save} disabled={saving}
                data-testid="location-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </Block>
  );
}


// ─────────────── Slot & package quick edit ───────────────

// ─────────────── Eligible packages cards (radio cards with resolved price) ───────────────

/**
 * "Compare packages" CTA + side-by-side modal. Helps the rep walk the
 * customer through what they get at each tier — column-per-package with
 * a green ✓ on every item that's included. Click any package's "Select"
 * inside the modal to pick it without leaving the comparison view.
 */
function ComparePackagesButton({ packages, onPick, currentPackageId }) {
  const [open, setOpen] = useState(false);
  // Build the union of all unique items (preserving the order found in
  // the package with the most items so customers see the natural service
  // progression from light → deep).
  const itemRows = useMemo(() => {
    const seen = new Map();
    // Pick the longest package as ordering anchor
    const sorted = [...packages].sort((a, b) => (b.items?.length || 0) - (a.items?.length || 0));
    sorted.forEach(pkg => {
      (pkg.items || []).forEach(it => {
        const key = (it.name || '').trim().toLowerCase();
        if (!key) return;
        if (!seen.has(key)) seen.set(key, it.name.trim());
      });
    });
    return Array.from(seen.values());
  }, [packages]);

  const includes = (pkg, itemName) => {
    const key = itemName.trim().toLowerCase();
    return (pkg.items || []).some(it => (it.name || '').trim().toLowerCase() === key);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline flex items-center gap-1"
        data-testid="compare-packages-btn"
      >
        <Sparkles className="h-3 w-3" /> Compare packages
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="compare-packages-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Compare service packages</DialogTitle>
            <p className="text-xs text-slate-500">
              Walk your customer through each tier — green ✓ shows what's included.
            </p>
          </DialogHeader>

          <div className="overflow-auto flex-1">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-emerald-500 text-white z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold w-[34%] border-r border-emerald-400">Service item</th>
                  {packages.map(pkg => (
                    <th key={pkg.id} className="text-left px-3 py-2 font-semibold border-r border-emerald-400 last:border-r-0" data-testid={`compare-col-${pkg.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-[15px] leading-tight">{pkg.name}</div>
                          <div className="text-[11px] font-normal opacity-90 mt-0.5">
                            {pkg.resolved_price != null && pkg.resolved_price > 0
                              ? fmtINR(pkg.resolved_price)
                              : 'Price on request'}
                          </div>
                        </div>
                        {pkg.id === currentPackageId ? (
                          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-white text-emerald-700">
                            Selected
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={async () => { await onPick(pkg); setOpen(false); }}
                            className="bg-white text-orange-700 hover:bg-orange-50 h-7"
                            data-testid={`compare-select-${pkg.id}`}
                          >
                            Select
                          </Button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemRows.map((itemName, idx) => (
                  <tr key={itemName} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-3 py-2 border-b border-r border-slate-200 text-slate-800 font-medium">
                      {itemName}
                    </td>
                    {packages.map(pkg => {
                      const has = includes(pkg, itemName);
                      return (
                        <td
                          key={pkg.id}
                          className="px-3 py-2 border-b border-r border-slate-200 last:border-r-0 text-center"
                          data-testid={`compare-cell-${pkg.id}-${itemName.replace(/\W+/g, '_').slice(0, 30)}`}
                        >
                          {has ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                              ✓
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Footer summary row: item count per tier */}
                <tr className="bg-slate-100 font-semibold">
                  <td className="px-3 py-2 border-r border-slate-300 text-slate-700">
                    Total items
                  </td>
                  {packages.map(pkg => (
                    <td key={pkg.id} className="px-3 py-2 border-r border-slate-300 last:border-r-0 text-center text-slate-700">
                      {(pkg.items || []).length}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="compare-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function EligiblePackageCards({ lead, onChanged, currentPackageId = null }) {
  const v = lead.vehicle || {};
  const ready = !!(v.brand && v.model && v.fuel_type);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(null);   // package id being selected
  // The selected card auto-expands its items below; clicking another card
  // collapses the previous expansion.
  const [expandedId, setExpandedId] = useState(currentPackageId || null);

  useEffect(() => {
    setExpandedId(currentPackageId || null);
  }, [currentPackageId]);

  useEffect(() => {
    if (!ready) {
      setPackages([]);
      return;
    }
    setLoading(true);
    servicePackagesApi
      .eligible({
        brand: v.brand,
        model: v.model,
        fuel_type: (v.fuel_type || '').toUpperCase(),
        car_type: v.body_class || undefined,
        channel: 'crm',
      })
      .then((r) => setPackages(r.data?.packages || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [ready, v.brand, v.model, v.fuel_type, v.body_class]);

  const pick = async (pkg) => {
    if (pkg.id === currentPackageId) {
      // Already the selected package — no-op (don't fire a network request)
      toast.info(`${pkg.name} is already selected`);
      return;
    }
    setPicking(pkg.id);
    try {
      await serviceLeadsApi.patch(lead.id, { package_id: pkg.id });
      toast.success(`Selected: ${pkg.name}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not select package');
    } finally { setPicking(null); }
  };

  if (!ready) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="eligible-need-vehicle">
        Set the vehicle first (Make + Model + Fuel) — then matching packages with their prices appear here.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-2 py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding eligible packages…
      </div>
    );
  }

  if (!packages.length) {
    return (
      <div className="text-xs text-gray-500 italic">
        No active CRM packages found. Ask admin to publish one.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="eligible-package-cards">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
          Recommended packages for {v.brand} {v.model} · {v.fuel_type}
        </div>
        {packages.length >= 2 && (
          <ComparePackagesButton packages={packages} onPick={pick} currentPackageId={currentPackageId} />
        )}
      </div>
      {packages.map((pkg) => {
        const isExpanded = expandedId === pkg.id;
        const itemCount = (pkg.items || []).length;
        const hasPrice = pkg.resolved_price != null && pkg.resolved_price > 0;
        const isPicking = picking === pkg.id;
        const isSelected = pkg.id === currentPackageId;
        return (
          <div
            key={pkg.id}
            className={`rounded-lg p-2.5 transition-colors border-2 ${
              isSelected
                ? 'border-orange-500 bg-orange-50/60 ring-1 ring-orange-200'
                : 'border-slate-200 hover:border-orange-400'
            }`}
            data-testid={`eligible-package-${pkg.id}`}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sm text-slate-900">{pkg.name}</div>
                  <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                    {pkg.category}
                  </span>
                  {isSelected && (
                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-orange-600 text-white" data-testid={`pkg-selected-badge-${pkg.id}`}>
                      Selected
                    </span>
                  )}
                </div>
                {pkg.description && (
                  <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{pkg.description}</div>
                )}
                <div className="mt-1.5">
                  {hasPrice ? (
                    <span className="text-base font-bold text-emerald-700">
                      {fmtINR(pkg.resolved_price)}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 font-medium" data-testid={`pkg-no-price-${pkg.id}`}>
                      Price on request
                    </span>
                  )}
                  {itemCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : pkg.id)}
                      className="ml-2 text-[11px] text-blue-600 hover:underline"
                      data-testid={`pkg-toggle-items-${pkg.id}`}
                    >
                      {isExpanded ? 'Hide' : 'What\u2019s covered'} ({itemCount})
                    </button>
                  )}
                </div>
                {isExpanded && itemCount > 0 && (
                  <ul className="mt-2 pl-3 border-l-2 border-emerald-200 space-y-0.5" data-testid={`pkg-items-${pkg.id}`}>
                    {pkg.items.map((it, i) => (
                      <li key={i} className="text-xs">
                        <span className="text-emerald-600">✓</span>{' '}
                        <span className="text-slate-800 font-medium">{it.name}</span>
                        {it.description && (
                          <span className="text-slate-500"> — {it.description}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => pick(pkg)}
                disabled={isPicking || isSelected}
                variant={isSelected ? 'outline' : 'default'}
                className={isSelected
                  ? 'shrink-0 border-orange-300 text-orange-700'
                  : 'bg-orange-600 hover:bg-orange-700 shrink-0'}
                data-testid={`pkg-select-${pkg.id}`}
              >
                {isPicking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isSelected ? 'Selected' : 'Select')}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function SlotPackageEdit({ lead, onChanged }) {
  const [edit, setEdit] = useState(false);

  if (!edit) {
    return (
      <Button variant="outline" size="sm" className="w-full mt-2"
              onClick={() => setEdit(true)} data-testid="open-slot-package-edit">
        <Pencil className="h-3 w-3 mr-1" /> Change package
      </Button>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-3 mt-2 space-y-3" data-testid="slot-package-edit-panel">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
        Change package
      </div>
      {/* Cards on top — clicking auto-patches package_id and reloads the lead.
          NOTE: the duplicate "Preferred slot" picker that used to live here was
          removed on 2026-05-18 — slot is edited from the PreferredSlotBlock in
          the left column. */}
      <EligiblePackageCards lead={lead} onChanged={onChanged} currentPackageId={lead.package_id} />

      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" className="flex-1"
                onClick={() => setEdit(false)}>Close</Button>
      </div>
    </div>
  );
}



// ─────────────── Preferred slot quick-edit block ───────────────

function PreferredSlotBlock({ lead, onChanged }) {
  const [slot, setSlot] = useState(lead.preferred_slot || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSlot(lead.preferred_slot || ''); }, [lead.preferred_slot]);

  const save = async () => {
    if (!slot || slot === lead.preferred_slot) return;
    setSaving(true);
    try {
      await serviceLeadsApi.patch(lead.id, { preferred_slot: slot });
      toast.success('Preferred slot updated');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save slot');
    } finally { setSaving(false); }
  };

  return (
    <Block label="Preferred slot" icon={CalendarCheck}>
      <Input
        type="datetime-local"
        value={slot}
        onChange={e => setSlot(e.target.value)}
        onBlur={save}
        data-testid="preferred-slot-input"
      />
      <p className="text-[11px] text-gray-500 mt-1">
        Customer&apos;s preferred service date &amp; time. This is locked into the WhatsApp confirmation.
      </p>
      {saving && (
        <div className="text-[11px] text-gray-400 mt-1 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </div>
      )}
    </Block>
  );
}


// ─────────────── Activity-only modal (opened via the Activity column button) ───────────────

export function ActivityDialog({ leadId, onOpenChange }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await serviceLeadsApi.get(leadId);
      setLead(r.data);
    } catch {
      toast.error('Failed to load activities');
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => {
    if (!leadId) { setLead(null); return; }
    reload();
  }, [leadId, reload]);

  return (
    <Dialog open={!!leadId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0"
                     data-testid="activity-modal">
        <DialogHeader className="px-5 py-3 sticky top-0 bg-blue-600 z-10 flex flex-row items-center justify-between rounded-t-lg">
          <DialogTitle className="flex items-center gap-2 text-white">
            <StickyNote className="h-4 w-4" />
            Activity
            {lead && <span className="text-xs text-blue-100 font-normal">· {lead.customer_name}</span>}
          </DialogTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full hover:bg-blue-500 p-1.5 text-white"
            aria-label="Close"
            data-testid="activity-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>
        <div className="p-5">
          {loading || !lead ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1"
                   data-testid="activity-timeline">
                {(lead.activities || []).slice().reverse().map(a => (
                  <div key={a.id} className="border-l-2 border-blue-200 pl-3 py-1.5">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="font-medium uppercase tracking-wide">
                        {(a.type || '').replace(/_/g, ' ')}
                      </span>
                      <span>{timeAgo(a.at)}</span>
                    </div>
                    {a.note && <div className="text-gray-700 mt-0.5 text-sm">{a.note}</div>}
                    {a.outcome && !a.note && <div className="text-gray-700 mt-0.5 text-sm">Outcome: {a.outcome}</div>}
                    {a.by && <div className="text-[11px] text-gray-400 mt-0.5">by {a.by}</div>}
                  </div>
                ))}
                {(lead.activities || []).length === 0 && (
                  <div className="text-xs text-gray-400 text-center py-4">No activity yet</div>
                )}
              </div>
              <div className="border-t mt-3 pt-3">
                <AddNoteInline leadId={lead.id} onDone={reload} />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────── Customer history (all past leads for this mobile) ───────────────

function CustomerHistory({ mobile, excludeId, onSwitchLead, onLeadDeleted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return serviceLeadsApi.byCustomer(mobile, { exclude: excludeId })
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [mobile, excludeId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    serviceLeadsApi.byCustomer(mobile, { exclude: excludeId })
      .then((r) => alive && setRows(r.data?.rows || []))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [mobile, excludeId]);

  // Split by kind (backend tags each row): drafts + active are mergeable,
  // terminal rows are immutable past services.
  const mergeable = rows.filter((r) => r.kind === 'draft' || r.kind === 'active');
  const terminal = rows.filter((r) => r.kind === 'terminal');

  const mergeDupes = async () => {
    // window.confirm is unreliable in iframes/some browsers — degrade
    // gracefully but still gate destructive action.
    let confirmed = true;
    try {
      confirmed = window.confirm(
        `Merge ${mergeable.length + 1} open lead(s) for ${mobile} (this lead + ${mergeable.length} duplicate${mergeable.length > 1 ? 's' : ''}) into the most-recently-updated one?\n\n` +
        'This collapses package, vehicle, slot, history and activities into a single row. ' +
        'Past services (Completed/Cancelled/Dead) are NOT touched.\n\nProceed?',
      );
    } catch (_) {
      confirmed = true;
    }
    if (!confirmed) return;
    setMerging(true);
    // eslint-disable-next-line no-console
    console.log('[merge-dupes] firing dedupe for mobile=', mobile);
    try {
      const r = await serviceLeadsApi.dedupeCustomer(mobile);
      const result = r.data || {};
      // eslint-disable-next-line no-console
      console.log('[merge-dupes] response', result);
      if ((result.merged ?? 0) === 0) {
        toast.info('No duplicates to merge — only one active row exists for this mobile.');
      } else {
        toast.success(
          `Merged ${result.merged} duplicate(s). Kept lead ${(result.kept_id || '').slice(0, 8)} (${result.kept_status || '—'}).`,
        );
      }
      await reload();
      window.dispatchEvent(new CustomEvent('refresh-service-leads'));
      // If the currently-open lead got merged away (keeper is a different id),
      // tell parent to close/swap the modal so the rep isn't stranded on a
      // ghost record.
      if (result.kept_id && excludeId && result.kept_id !== excludeId) {
        onLeadDeleted?.(result.kept_id);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[merge-dupes] failed', e);
      const msg = e?.response?.data?.detail || e?.message || 'Merge failed';
      toast.error(`Merge failed: ${msg}`);
    } finally {
      setMerging(false);
    }
  };

  if (loading) {
    return (
      <Block label="Customer history" icon={ClipboardList}>
        <div className="text-xs text-gray-400 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      </Block>
    );
  }
  if (rows.length === 0) {
    return (
      <Block label="Customer history" icon={ClipboardList}>
        <div className="text-xs text-gray-500">First-time customer — no past services.</div>
      </Block>
    );
  }
  return (
    <Block label={`Customer history (${rows.length})`} icon={ClipboardList}>
      {mergeable.length > 0 && (
        <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5"
             data-testid="duplicate-leads-alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-amber-900">
                {mergeable.length} active duplicate{mergeable.length > 1 ? 's' : ''} for this mobile
              </div>
              <div className="text-[11px] text-amber-800 mt-0.5 leading-snug">
                The same customer has {mergeable.length + 1} active rows. Merge them so
                package, status and activity history stay in one place.
              </div>
              <button
                type="button"
                onClick={mergeDupes}
                disabled={merging}
                className="mt-2 text-[11px] font-medium px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white inline-flex items-center gap-1.5"
                data-testid="merge-duplicates-btn"
              >
                {merging ? <Loader2 className="h-3 w-3 animate-spin" /> : <Layers className="h-3 w-3" />}
                {merging ? 'Merging…' : `Merge ${mergeable.length} duplicate${mergeable.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {mergeable.length > 0 && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mt-1 mb-1">
          Active duplicates ({mergeable.length})
        </div>
      )}
      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="customer-history">
        {mergeable.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onSwitchLead?.(r.id)}
            className="w-full text-left rounded border bg-white hover:bg-blue-50 hover:border-blue-200 p-2 transition-colors"
            data-testid={`history-row-${r.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-800 truncate">
                {r.package_name || 'No package'}
              </span>
              <Badge className={`${STATUS_BADGE[r.status] || ''} border text-[10px] flex-shrink-0`}>
                {renderStatus(r.status)}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
              <span className="truncate">
                {(r.vehicle?.brand || '')} {(r.vehicle?.model || '')}
                {r.vehicle?.rc_number ? ` (${r.vehicle.rc_number})` : ''}
              </span>
              <span className="flex-shrink-0">
                {r.final_price != null ? fmtINR(r.final_price) : '—'}
              </span>
            </div>
            <div className="text-[10px] text-gray-400">
              {timeAgo(r.created_at)}
            </div>
          </button>
        ))}

        {terminal.length > 0 && (
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mt-3 mb-1 border-t pt-2">
            Past services ({terminal.length})
          </div>
        )}
        {terminal.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onSwitchLead?.(r.id)}
            className="w-full text-left rounded border bg-slate-50 hover:bg-slate-100 hover:border-slate-300 p-2 transition-colors opacity-90"
            data-testid={`history-row-${r.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-slate-700 truncate">
                {r.package_name || 'No package'}
              </span>
              <Badge className={`${STATUS_BADGE[r.status] || ''} border text-[10px] flex-shrink-0`}>
                {renderStatus(r.status)}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
              <span className="truncate">
                {(r.vehicle?.brand || '')} {(r.vehicle?.model || '')}
                {r.vehicle?.rc_number ? ` (${r.vehicle.rc_number})` : ''}
              </span>
              <span className="flex-shrink-0">
                {r.final_price != null ? fmtINR(r.final_price) : '—'}
              </span>
            </div>
            <div className="text-[10px] text-gray-400">
              {timeAgo(r.created_at)}
            </div>
          </button>
        ))}
      </div>
    </Block>
  );
}

// Inbox-level banner — surfaces total customers with active duplicates and
// offers a one-click "Merge All" sweep. Visible only to admin-tier roles
// (CEO / SALES_HEAD / COUNTRY_HEAD / ADMIN). Auto-refreshes after each merge.
function DuplicateCustomersBanner({ currentUser }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  // Preview modal state — null = closed, object = open with preview data
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Per-mobile keeper override map: { [mobile]: keeperLeadId }. Rep clicks
  // a row in the modal to promote it to KEEP. Empty = use backend auto-pick.
  const [keeperOverrides, setKeeperOverrides] = useState({});
  // Post-merge result modal
  const [mergeResult, setMergeResult] = useState(null);
  const role = currentUser?.role_code || '';
  const isAdmin = ['CEO', 'COUNTRY_HEAD', 'SALES_HEAD', 'ADMIN'].includes(role);

  const load = useCallback(() => {
    if (!isAdmin) return;
    serviceLeadsApi.duplicateCustomers()
      .then((r) => setSummary(r.data || null))
      .catch(() => setSummary(null));
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('refresh-service-leads', handler);
    return () => window.removeEventListener('refresh-service-leads', handler);
  }, [load]);

  if (!isAdmin || !summary || (summary.customers_with_duplicates ?? 0) === 0) {
    return null;
  }

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await serviceLeadsApi.duplicateCustomersPreview();
      setPreview(r.data || { groups: [], customers_with_duplicates: 0, rows_to_merge: 0 });
      setKeeperOverrides({});  // reset on each fresh open
    } catch (e) {
      toast.error(`Failed to load preview: ${e?.response?.data?.detail || e?.message || 'unknown'}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Effective keeper id for a group — override wins, else fall back to
  // the backend-picked keeper.
  const effectiveKeeperId = (g) => keeperOverrides[g.mobile] || g.keeper?.id;

  // Toggle which row is the KEEP for this mobile. Clicking the current
  // keeper is a no-op (must always have one keeper per group).
  const setKeeperFor = (mobile, leadId) => {
    setKeeperOverrides((prev) => ({ ...prev, [mobile]: leadId }));
  };

  const confirmSweep = async () => {
    setBusy(true);
    try {
      // Only send overrides that differ from the backend's auto-pick — keeps
      // the payload small and the auto-pick wins for groups the rep didn't touch.
      const overrides = {};
      for (const g of (preview?.groups || [])) {
        const chosen = keeperOverrides[g.mobile];
        if (chosen && chosen !== g.keeper?.id) {
          overrides[g.mobile] = chosen;
        }
      }
      const r = await serviceLeadsApi.dedupeAllCustomers(false, overrides);
      const result = r.data || {};
      // Show the post-merge detail modal so the rep has full audit trail
      setMergeResult({
        customers: result.customers_with_duplicates ?? 0,
        rows: result.rows_merged_or_to_merge ?? 0,
        details: result.details || preview?.groups || [],
      });
      setPreview(null);
      setKeeperOverrides({});
      window.dispatchEvent(new CustomEvent('refresh-service-leads'));
    } catch (e) {
      toast.error(`Sweep failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="px-4 py-3 border-amber-300 bg-amber-50" data-testid="dup-customers-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <Users className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-semibold text-amber-900">
                {summary.customers_with_duplicates} customer{summary.customers_with_duplicates > 1 ? 's' : ''} {summary.customers_with_duplicates > 1 ? 'have' : 'has'} duplicate active leads
              </div>
              <div className="text-xs text-amber-800">
                {summary.rows_to_merge} duplicate row{summary.rows_to_merge > 1 ? 's' : ''} can be merged into one row each. Past services stay untouched.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={openPreview}
            disabled={previewLoading}
            className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="merge-all-duplicates-btn"
          >
            {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />}
            {previewLoading ? 'Loading…' : `Review & Merge (${summary.rows_to_merge})`}
          </Button>
        </div>
      </Card>

      {/* PREVIEW MODAL — shows per-customer keeper-vs-losers before merging */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0"
                       data-testid="merge-preview-modal">
          <DialogHeader className="px-5 py-4 border-b bg-amber-50">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4 text-amber-600" />
              Review duplicate merge
            </DialogTitle>
            <p className="text-xs text-gray-600 mt-1">
              <span className="font-semibold">{preview?.customers_with_duplicates || 0}</span> customer(s) ·
              <span className="font-semibold ml-1">{preview?.rows_to_merge || 0}</span> duplicate row(s) will be deleted.
              For each customer, the <span className="text-emerald-700 font-semibold">green</span> row is KEPT
              and the <span className="text-rose-700 font-semibold">red</span> rows are merged INTO it.
              <span className="block mt-1 text-amber-700">
                💡 Tip: click any row to choose which lead to keep. The keeper inherits all package /
                vehicle / history data from the merged rows.
              </span>
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
            {(preview?.groups || []).length === 0 && (
              <div className="text-sm text-gray-500 py-8 text-center">No duplicates to merge.</div>
            )}
            {(preview?.groups || []).map((g) => {
              const allRows = [g.keeper, ...g.losers];
              const keepId = effectiveKeeperId(g);
              return (
                <div key={g.mobile} className="border rounded-lg overflow-hidden"
                     data-testid={`merge-group-${g.mobile}`}>
                  <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                    <div className="text-xs">
                      <Phone className="inline h-3 w-3 mr-1 text-gray-500" />
                      <span className="font-semibold text-gray-800">{g.mobile}</span>
                      <span className="text-gray-500 ml-2">
                        · {g.loser_count + 1} active rows · {g.loser_count} will be deleted
                      </span>
                    </div>
                    {keepId !== g.keeper?.id && (
                      <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[10px]"
                             data-testid={`keeper-overridden-${g.mobile}`}>
                        Keeper changed
                      </Badge>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left w-16">Action</th>
                        <th className="px-3 py-1.5 text-left">Name</th>
                        <th className="px-3 py-1.5 text-left">Status</th>
                        <th className="px-3 py-1.5 text-left">Package</th>
                        <th className="px-3 py-1.5 text-left">Vehicle</th>
                        <th className="px-3 py-1.5 text-left">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allRows.map((r) => {
                        const isKeep = r.id === keepId;
                        return (
                          <tr
                            key={r.id}
                            onClick={() => { if (!isKeep) setKeeperFor(g.mobile, r.id); }}
                            className={`${isKeep ? 'bg-emerald-50/60' : 'bg-rose-50/30 cursor-pointer hover:bg-rose-100/50'} transition-colors`}
                            data-testid={`merge-row-${r.id}`}
                            title={isKeep ? 'This row will be kept' : 'Click to keep this row instead'}
                          >
                            <td className="px-3 py-2">
                              {isKeep ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]"
                                       data-testid={`keep-badge-${r.id}`}>
                                  ✓ KEEP
                                </Badge>
                              ) : (
                                <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[10px]"
                                       data-testid={`merge-badge-${r.id}`}>
                                  ✗ MERGE
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-800">
                              {r.customer_name || <span className="italic text-gray-400">(no name)</span>}
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={`${STATUS_BADGE[r.status] || ''} border text-[10px]`}>
                                {renderStatus(r.status)}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-gray-700">{r.package_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-700">
                              {[r.vehicle?.brand, r.vehicle?.model].filter(Boolean).join(' ') || '—'}
                              {r.vehicle?.rc_number && <div className="text-[10px] text-gray-400">{r.vehicle.rc_number}</div>}
                            </td>
                            <td className="px-3 py-2 text-gray-500">{timeAgo(r.updated_at || r.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          <DialogFooter className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-500">
              ⚠ Once confirmed, the red rows are permanently deleted. Their package / vehicle / status history
              is absorbed into the green keeper. Past services (Completed / Cancelled) are NEVER touched.
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <Button variant="outline" onClick={() => setPreview(null)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={confirmSweep} disabled={busy || (preview?.groups || []).length === 0}
                      className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                      data-testid="confirm-merge-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />}
                {busy ? 'Merging…' : `Confirm merge (${preview?.rows_to_merge || 0})`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POST-MERGE RESULT MODAL */}
      <Dialog open={!!mergeResult} onOpenChange={(v) => { if (!v) setMergeResult(null); }}>
        <DialogContent className="max-w-md" data-testid="merge-result-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Merge complete
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <p>
              Successfully merged duplicates for{' '}
              <span className="font-semibold">{mergeResult?.customers || 0}</span> customer(s).
            </p>
            <p>
              <span className="font-semibold text-rose-700">{mergeResult?.rows || 0}</span> duplicate row(s)
              were deleted; their package / vehicle / history was absorbed into the kept row.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-900">
              ✓ The inbox has been refreshed. Past services (Completed / Cancelled / Dead) were not touched.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setMergeResult(null)} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="merge-result-close">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



// ────────────────────────────────────────────────────────────────────
// Service Leads — Row Enhancement Components (May 2026)
// 1. AssignedToCell — clickable name → opens ReassignPopover
// 2. ReassignPopover — in-city / out-of-city buckets with toggle
// 3. NoteModal — quick free-text note that posts to /activities
// 4. ActivityDrawer — right-side slide-in timeline of every event
// ────────────────────────────────────────────────────────────────────

const SALES_ADMIN_ROLES = new Set(['CEO', 'CTO', 'CTOO', 'COUNTRY_HEAD', 'SALES_HEAD', 'ADMIN']);

function canReassign(user, lead) {
  const role = user?.role_code || '';
  if (SALES_ADMIN_ROLES.has(role)) return true;
  if (lead?.assigned_to && lead.assigned_to === user?.id) return true;
  return false;
}

function AssignedToCell({ lead, currentUser, onReassign }) {
  const allowed = canReassign(currentUser, lead);
  const name = lead.assigned_to_name;
  const crossCity = lead.assignment_note && lead.assignment_note.toLowerCase().includes('cross-city');

  if (!name) {
    return (
      <button
        disabled={!allowed}
        onClick={onReassign}
        className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline flex items-center gap-1"
        data-testid={`assign-empty-${lead.id}`}
      >
        <UserPlus className="h-3 w-3" /> Assign
      </button>
    );
  }
  return (
    <button
      disabled={!allowed}
      onClick={onReassign}
      title={allowed ? 'Click to reassign' : 'Only admins or the assigned rep can reassign'}
      className={`group flex items-center gap-1.5 text-xs ${allowed ? 'hover:bg-indigo-50 cursor-pointer' : 'cursor-default'} rounded px-1.5 py-1 -mx-1`}
      data-testid={`assigned-${lead.id}`}
    >
      <span className="h-5 w-5 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-[10px] font-semibold text-white flex items-center justify-center flex-shrink-0">
        {name.slice(0, 1).toUpperCase()}
      </span>
      <span className="text-gray-800 font-medium truncate max-w-[110px]">{name}</span>
      {crossCity && (
        <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0" title="Cross-city assignment" />
      )}
      {allowed && (
        <UserCog className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

// 2026-06-03: Reminder column cell — surfaces the upcoming WhatsApp
// reminder + lets the rep set/edit/clear it inline.
function ReminderCell({ lead, onSet, onClear }) {
  const hasReminder = !!lead.reminder_date;
  if (!hasReminder) {
    return (
      <button
        onClick={onSet}
        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        data-testid={`reminder-set-${lead.id}`}
      >
        <Bell className="h-3 w-3" />
        Set
      </button>
    );
  }
  const when = lead.reminder_time
    ? `${lead.reminder_date} ${lead.reminder_time}`
    : lead.reminder_date;
  // Has the scheduler already fired the WhatsApp?  (or skipped due to permanent failure)
  const fired = !!lead.reminder_notified;
  const skipReason = lead.reminder_notify_skipped_reason;
  return (
    <div
      className="flex flex-col gap-0.5 text-xs"
      data-testid={`reminder-cell-${lead.id}`}
    >
      <button
        onClick={onSet}
        className={`flex items-center gap-1 hover:underline ${fired ? 'text-emerald-700' : 'text-amber-700'}`}
        title={fired
          ? (skipReason ? `Skipped: ${skipReason}` : 'WhatsApp sent to rep')
          : 'Click to edit'}
        data-testid={`reminder-edit-${lead.id}`}
      >
        {fired
          ? <CheckCircle2 className="h-3 w-3 text-emerald-600 flex-shrink-0" />
          : <Clock className="h-3 w-3 text-amber-600 flex-shrink-0" />}
        <span className="font-medium truncate max-w-[120px]">{when}</span>
      </button>
      {lead.reminder_reason && (
        <span className="text-[10px] text-gray-500 truncate max-w-[140px]">
          {lead.reminder_reason}
        </span>
      )}
      <button
        onClick={onClear}
        className="text-[10px] text-gray-400 hover:text-red-600 self-start flex items-center gap-0.5"
        title="Cancel reminder"
        data-testid={`reminder-clear-${lead.id}`}
      >
        <X className="h-2.5 w-2.5" /> clear
      </button>
    </div>
  );
}

function ReassignPopover({ lead, currentUser, onClose, onDone }) {
  const [reps, setReps] = useState({ in_city: [], out_of_city: [] });
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setLoading(true);
    setNote('');
    setShowAll(false);
    serviceLeadsApi.eligibleReps(lead.city)
      .then((r) => setReps(r.data || { in_city: [], out_of_city: [] }))
      .catch(() => toast.error('Failed to load eligible reps'))
      .finally(() => setLoading(false));
  }, [lead]);

  if (!lead) return null;
  const allowed = canReassign(currentUser, lead);

  const doAssign = async (rep, isCrossCity) => {
    if (isCrossCity) {
      const ok = window.confirm(
        `${rep.name} doesn't cover ${lead.city}. Their service cities: ${(rep.service_cities || []).join(', ') || 'none'}.\n\nProceed with cross-city assignment? An audit note will be auto-stamped.`,
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await serviceLeadsApi.reassign(lead.id, { assigned_to: rep.id, note: note || undefined });
      toast.success(`Reassigned to ${rep.name}`);
      onDone({ ...lead, assigned_to: rep.id, assigned_to_name: rep.name, assignment_note: note || null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-0" data-testid="reassign-popover">
        <DialogHeader className="px-5 py-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserCog className="h-4 w-4 text-indigo-600" />
            Reassign · <span className="text-gray-500 font-normal text-sm">{lead.customer_name}</span>
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            Currently: <span className="font-medium text-gray-700">{lead.assigned_to_name || 'Unassigned'}</span>
            {lead.city && <> · City: <span className="font-medium text-gray-700">{lead.city}</span></>}
          </p>
        </DialogHeader>

        {!allowed && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            View-only. Only CEO / Country Head / Sales Head / Admin or the currently-assigned rep can reassign.
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto px-5 py-3 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">
                  In-city ({reps.in_city.length})
                </p>
                {reps.in_city.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2">No reps cover this city.</p>
                ) : (
                  <div className="space-y-1">
                    {reps.in_city.map((rep) => (
                      <button key={rep.id}
                        disabled={!allowed || submitting || rep.id === lead.assigned_to}
                        onClick={() => doAssign(rep, false)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-emerald-50 disabled:opacity-50 disabled:hover:bg-transparent text-left"
                        data-testid={`reassign-rep-${rep.id}`}>
                        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-[10px] font-semibold text-white flex items-center justify-center flex-shrink-0">
                          {rep.name?.slice(0, 1).toUpperCase()}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-800 truncate block">{rep.name}</span>
                          {rep.email && <span className="text-[10px] text-gray-400 truncate block">{rep.email}</span>}
                        </span>
                        {rep.id === lead.assigned_to && (
                          <span className="text-[10px] text-emerald-600 font-medium">current</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {reps.out_of_city.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowAll((s) => !s)}
                    className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                    data-testid="reassign-show-all-toggle">
                    {showAll ? 'Hide' : 'Show'} all reps ({reps.out_of_city.length} out-of-city)
                    <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
                  </button>
                  {showAll && (
                    <div className="space-y-1 mt-2">
                      <div className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        Out-of-city assignment auto-stamps an audit note.
                      </div>
                      {reps.out_of_city.map((rep) => (
                        <button key={rep.id}
                          disabled={!allowed || submitting}
                          onClick={() => doAssign(rep, true)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-amber-50 disabled:opacity-50 text-left"
                          data-testid={`reassign-rep-out-${rep.id}`}>
                          <span className="h-6 w-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-[10px] font-semibold text-white flex items-center justify-center flex-shrink-0">
                            {rep.name?.slice(0, 1).toUpperCase()}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="text-sm text-gray-800 truncate block">{rep.name}</span>
                            <span className="text-[10px] text-gray-400 truncate block">
                              {(rep.service_cities || []).join(', ') || 'no cities'}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 border-t">
                <Label className="text-[10px] text-gray-500">Reassignment note (optional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Rep on leave / customer requested change"
                  className="text-xs mt-1" data-testid="reassign-note" />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteModal() {
  // Deprecated: notes are now part of the unified ActivityDrawer (Notes tab).
  // Kept as a no-op shim in case any external import still references it.
  return null;
}

// ── Activity timeline classification ──
const ACTIVITY_META = {
  created:          { icon: Plus,            color: 'text-blue-600 bg-blue-100',         label: 'Lead created' },
  draft_upgraded:   { icon: ShieldCheck,     color: 'text-emerald-600 bg-emerald-100',   label: 'OTP verified · draft upgraded' },
  otp_verified:     { icon: ShieldCheck,     color: 'text-emerald-600 bg-emerald-100',   label: 'OTP verified' },
  auto_assigned:    { icon: Sparkles,        color: 'text-purple-600 bg-purple-100',     label: 'Auto-assigned' },
  reassigned:       { icon: UserCog,         color: 'text-indigo-600 bg-indigo-100',     label: 'Reassigned' },
  status_changed:   { icon: ArrowRight,      color: 'text-slate-700 bg-slate-100',       label: 'Status changed' },
  edited:           { icon: Pencil,          color: 'text-gray-600 bg-gray-100',         label: 'Edited' },
  note:             { icon: MessageSquarePlus, color: 'text-amber-600 bg-amber-100',     label: 'Note' },
  call_attempt:     { icon: Phone,           color: 'text-blue-600 bg-blue-100',         label: 'Call attempt' },
  quoted:           { icon: Tag,             color: 'text-emerald-600 bg-emerald-100',   label: 'Quote sent' },
  booked:           { icon: CheckCircle2,    color: 'text-emerald-700 bg-emerald-100',   label: 'Booked' },
  payment_link_sent:{ icon: Send,            color: 'text-blue-700 bg-blue-100',         label: 'Payment link sent' },
  cancelled:        { icon: X,               color: 'text-red-600 bg-red-100',           label: 'Cancelled' },
  whatsapp_sent:    { icon: Send,            color: 'text-emerald-600 bg-emerald-100',   label: 'WhatsApp sent' },
  sms_sent:         { icon: Send,            color: 'text-blue-600 bg-blue-100',         label: 'SMS sent' },
  email_sent:       { icon: Mail,            color: 'text-purple-600 bg-purple-100',     label: 'Email sent' },
};

function ActivityRow({ act }) {
  const meta = ACTIVITY_META[act.type] || { icon: FileText, color: 'text-gray-500 bg-gray-100', label: act.type };
  const Icon = meta.icon;
  return (
    <div className="flex gap-3 py-2.5" data-testid={`activity-${act.type}`}>
      <div className={`h-7 w-7 rounded-full ${meta.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-900">{meta.label}</span>
          {act.from && act.to && (
            <span className="text-[10px] text-gray-600">
              <span className="text-gray-500">{act.from}</span>
              <ArrowRight className="inline h-2.5 w-2.5 mx-1 text-gray-400" />
              <span className="font-medium">{act.to}</span>
            </span>
          )}
          {act.outcome && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
              {act.outcome}
            </span>
          )}
        </div>
        {act.note && <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{act.note}</p>}
        {act.fields && (
          <p className="text-[10px] text-gray-400 mt-0.5">Updated: {act.fields.join(', ')}</p>
        )}
        <p className="text-[10px] text-gray-400 mt-1">
          {act.by ? <><span className="font-medium">{act.by}</span> · </> : <span className="italic">system · </span>}
          {act.at ? fmtDateTime(act.at) : (act.timestamp ? fmtDateTime(act.timestamp) : '')}
        </p>
      </div>
    </div>
  );
}

function ActivityDrawer({ lead, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);
  const [tab, setTab] = useState('activity');

  const refresh = useCallback(async () => {
    if (!lead) return;
    setLoading(true);
    try {
      const r = await serviceLeadsApi.get(lead.id);
      setDetail(r.data);
    } catch (e) {
      toast.error('Failed to load activity');
    } finally { setLoading(false); }
  }, [lead]);

  useEffect(() => { refresh(); setTab('activity'); }, [refresh]);

  if (!lead) return null;

  const allActivities = [...(detail?.activities || [])].sort((a, b) => {
    const ta = new Date(a.at || a.timestamp || 0).getTime();
    const tb = new Date(b.at || b.timestamp || 0).getTime();
    return tb - ta;
  });
  const notes = allActivities.filter((a) => a.type === 'note');
  const visible = tab === 'notes' ? notes : allActivities;

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      await serviceLeadsApi.addActivity(lead.id, { type: 'note', note: noteText.trim() });
      setNoteText('');
      // Land on the Notes tab right after posting so the user sees their note
      setTab('notes');
      await refresh();
      onChanged && onChanged();
      toast.success('Note added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally { setPosting(false); }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        data-testid="activity-backdrop"
      />
      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white z-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        data-testid="activity-drawer"
      >
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <HistoryIcon className="h-4 w-4 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{lead.customer_name}</h3>
              <p className="text-[11px] text-gray-500">{lead.mobile} · {lead.city || '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="activity-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b bg-gray-50">
          <div className="flex gap-1">
            {[
              { id: 'notes',    label: 'Notes',    icon: MessageSquarePlus, count: notes.length },
              { id: 'activity', label: 'Activity', icon: ActivityIcon,      count: allActivities.length },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`drawer-tab-${t.id}`}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  tab === t.id
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                }`}>{t.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center text-gray-400 py-12 text-sm" data-testid="activity-empty">
              {tab === 'notes' ? (
                <>
                  <MessageSquarePlus className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No notes yet. Add one below.
                </>
              ) : (
                <>
                  <ActivityIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No activity yet
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="activity-list">
              {visible.map((act, i) => <ActivityRow key={i} act={act} />)}
            </div>
          )}
        </div>

        {/* Quick note input at the bottom of the drawer */}
        <div className="px-5 py-3 border-t bg-gray-50 sticky bottom-0">
          <div className="flex items-center gap-2">
            <Input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && noteText.trim()) submitNote(); }}
              placeholder="Add a quick note..."
              className="text-xs flex-1"
              disabled={posting}
              data-testid="drawer-note-input"
            />
            <Button size="sm" onClick={submitNote} disabled={posting || !noteText.trim()} data-testid="drawer-note-submit">
              {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquarePlus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
